import React, { useState, useEffect, useRef, useCallback, createContext, useContext, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  ShoppingCart, MapPin, Phone, Clock, Plus, Minus, Trash2, Home, UtensilsCrossed,
  CheckCircle2, Loader2, X, ChevronRight, Mail, User, LogIn, LogOut, Heart,
  ClipboardList, Settings, Package, TrendingUp, Users, DollarSign, RefreshCw, MessageSquare, Edit2, CreditCard, AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Error Boundary to prevent blank screen crashes
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4" data-testid="error-boundary">
          <div className="max-w-md text-center bg-white p-8 rounded-2xl shadow-lg">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-serif font-bold mb-2">Oi, noe gikk galt</h1>
            <p className="text-muted-foreground mb-6">Beklager! En feil oppstod. Prøv å last inn siden på nytt.</p>
            <pre className="text-xs text-left bg-gray-100 p-3 rounded-lg overflow-auto max-h-32 mb-4 text-red-700">
              {this.state.error?.message || 'Ukjent feil'}
            </pre>
            <button 
              onClick={() => { localStorage.removeItem('cart'); localStorage.removeItem('orderType'); window.location.href = '/'; }}
              className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full py-3 font-medium"
              data-testid="error-reset-btn"
            >
              Tilbake til forsiden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Helper function to get correct image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    // Old format - convert to new format
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper: check if menu item is currently on active sale
const getActiveSale = (item) => {
  if (!item?.sale_price || item.sale_price >= item.price) return null;
  const today = new Date().toISOString().split('T')[0];
  if (item.sale_start && today < item.sale_start) return null;
  if (item.sale_end && today > item.sale_end) return null;
  return item.sale_price;
};

// Helper: effective price (sale price if active, else normal)
const getEffectivePrice = (item) => getActiveSale(item) ?? item.price;

// Auth Context
const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const res = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(res.data);
        } catch (e) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, phone, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, phone, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateFavorites = (favorites) => {
    setUser(prev => prev ? { ...prev, favorites } : null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, updateFavorites }}>
      {children}
    </AuthContext.Provider>
  );
};

// Cart Context
const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [orderType, setOrderType] = useState(() => {
    return localStorage.getItem('orderType') || 'today';
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('orderType', orderType);
  }, [orderType]);
  
  const addToCart = (item) => {
    const newMenuType = item.menuType || 'today';
    
    // Check if cart has items from a DIFFERENT menu type
    if (cart.length > 0) {
      const existingType = cart[0].menuType || orderType || 'today';
      if (existingType !== newMenuType) {
        const typeLabels = { today: 'I dag', later: 'Senere', soon: 'Snart', special: 'Skjervøy Daga' };
        const confirmMsg = `Du har allerede varer fra "${typeLabels[existingType]}"-menyen i handlekurven.\n\nVil du tømme handlekurven og legge til fra "${typeLabels[newMenuType]}"-menyen i stedet?\n\nDu kan kun bestille fra én meny om gangen.`;
        if (!window.confirm(confirmMsg)) {
          return false;
        }
        // Clear cart and add new item
        setCart([{ 
          menu_item_id: item.id, 
          name: item.name, 
          price: item.price, 
          quantity: 1,
          menuType: newMenuType
        }]);
        setOrderType(newMenuType);
        return true;
      }
    }
    
    // Save the menu type when adding item
    setOrderType(newMenuType);
    
    setCart(prev => {
      const existing = prev.find(i => i.menu_item_id === item.id);
      
      if (existing) {
        return prev.map(i => i.menu_item_id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { 
        menu_item_id: item.id, 
        name: item.name, 
        price: item.price, 
        quantity: 1,
        menuType: newMenuType
      }];
    });
    return true;
  };

  const removeFromCart = (itemId, scheduledDate = null, scheduledTime = null) => {
    setCart(prev => prev.filter(i => {
      if (scheduledDate) {
        return !(i.menu_item_id === itemId && i.scheduled_date === scheduledDate && i.scheduled_time === scheduledTime);
      }
      return i.menu_item_id !== itemId;
    }));
  };
  
  const updateQuantity = (itemId, quantity, scheduledDate = null, scheduledTime = null) => {
    if (quantity <= 0) { removeFromCart(itemId, scheduledDate, scheduledTime); return; }
    setCart(prev => prev.map(i => {
      if (scheduledDate) {
        if (i.menu_item_id === itemId && i.scheduled_date === scheduledDate && i.scheduled_time === scheduledTime) {
          return { ...i, quantity };
        }
        return i;
      }
      return i.menu_item_id === itemId ? { ...i, quantity } : i;
    }));
  };

  const clearCart = () => {
    setCart([]);
    setOrderType('today');
  };
  
  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, total, itemCount, orderType, setOrderType }}>
      {children}
    </CartContext.Provider>
  );
};

// Auth Dialog Component
const AuthDialog = ({ trigger }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Innlogget!');
      } else {
        await register(formData.name, formData.email, formData.phone, formData.password);
        toast.success('Konto opprettet!');
      }
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Noe gikk galt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {mode === 'login' ? 'Logg inn' : 'Opprett konto'}
          </DialogTitle>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Logg inn</TabsTrigger>
            <TabsTrigger value="register">Registrer</TabsTrigger>
          </TabsList>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {mode === 'register' && (
              <>
                <Input placeholder="Navn" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required data-testid="auth-name" />
                <Input type="tel" placeholder="Telefon" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required data-testid="auth-phone" />
              </>
            )}
            <Input type="email" placeholder="E-post" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required data-testid="auth-email" />
            <Input type="password" placeholder="Passord" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required data-testid="auth-password" />
            <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90" disabled={loading} data-testid="auth-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === 'login' ? 'Logg inn' : 'Registrer'}
            </Button>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// Header Component
const Header = () => {
  const { itemCount, cart, updateQuantity, removeFromCart, total } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cartOpen, setCartOpen] = useState(false);

  const handleCheckout = () => {
    setCartOpen(false);
    navigate('/checkout');
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur-md border-b border-border/40">
      <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-home-link">
          <span className="text-2xl md:text-3xl font-serif font-bold text-primary">Sydnie's</span>
          <span className="text-lg md:text-xl font-accent text-secondary">Meny</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          <button onClick={() => navigate('/')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-home">Hjem</button>
          <button onClick={() => navigate('/menu')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-menu">Meny</button>
          {user && <button onClick={() => navigate('/orders')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-orders">Mine bestillinger</button>}
          <button onClick={() => navigate('/contact')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-contact">Kontakt</button>
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              <button onClick={() => navigate('/profile')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="user-profile-btn">
                <User className="w-4 h-4" />
                {user.name}
              </button>
              <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <AuthDialog trigger={
              <Button variant="outline" size="sm" className="hidden md:flex" data-testid="login-btn">
                <LogIn className="w-4 h-4 mr-2" />
                Logg inn
              </Button>
            } />
          )}

          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="relative rounded-full px-4" data-testid="cart-button">
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && <Badge className="absolute -top-2 -right-2 bg-secondary text-white h-5 w-5 flex items-center justify-center p-0 text-xs">{itemCount}</Badge>}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-lg" data-testid="cart-sheet">
              <SheetHeader><SheetTitle className="font-serif text-2xl">Handlekurv</SheetTitle></SheetHeader>
              <div className="mt-6 flex flex-col h-[calc(100vh-200px)]">
                {cart.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                    <ShoppingCart className="w-16 h-16 mb-4 opacity-30" />
                    <p>Handlekurven er tom</p>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto space-y-4">
                      {cart.map(item => (
                        <div key={item.menu_item_id} className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl">
                          <div className="flex-1">
                            <h4 className="font-medium">{item.name}</h4>
                            <p className="text-secondary font-semibold">kr {item.price}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateQuantity(item.menu_item_id, item.quantity - 1)}><Minus className="w-4 h-4" /></Button>
                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={() => updateQuantity(item.menu_item_id, item.quantity + 1)}><Plus className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeFromCart(item.menu_item_id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-4" />
                    <div className="space-y-4">
                      <div className="flex justify-between text-lg font-semibold">
                        <span>Total</span>
                        <span className="text-secondary">kr {total}</span>
                      </div>
                      <Button className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full py-6 text-lg font-medium" onClick={handleCheckout} data-testid="checkout-button">
                        Gå til kassen<ChevronRight className="w-5 h-5 ml-2" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

// Mobile Navigation
const MobileNav = () => {
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { user } = useAuth();

  return (
    <nav className="md:hidden fixed bottom-6 left-4 right-4 bg-primary/95 backdrop-blur-lg text-white rounded-full p-4 shadow-2xl z-50 flex justify-around items-center border border-white/10" data-testid="mobile-nav">
      <button onClick={() => navigate('/')} className="flex flex-col items-center gap-1"><Home className="w-5 h-5" /><span className="text-xs">Hjem</span></button>
      <button onClick={() => navigate('/menu')} className="flex flex-col items-center gap-1"><UtensilsCrossed className="w-5 h-5" /><span className="text-xs">Meny</span></button>
      {user && <button onClick={() => navigate('/orders')} className="flex flex-col items-center gap-1"><ClipboardList className="w-5 h-5" /><span className="text-xs">Ordre</span></button>}
      <button onClick={() => navigate('/checkout')} className="relative flex flex-col items-center gap-1">
        <ShoppingCart className="w-5 h-5" /><span className="text-xs">Kurv</span>
        {itemCount > 0 && <Badge className="absolute -top-1 -right-1 bg-secondary text-white h-5 w-5 flex items-center justify-center p-0 text-xs">{itemCount}</Badge>}
      </button>
      {user ? (
        <button onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1"><User className="w-5 h-5" /><span className="text-xs">Profil</span></button>
      ) : (
        <AuthDialog trigger={<button className="flex flex-col items-center gap-1"><LogIn className="w-5 h-5" /><span className="text-xs">Logg inn</span></button>} />
      )}
    </nav>
  );
};

// Home Page
const HomePage = () => {
  const navigate = useNavigate();
  const [openingHours, setOpeningHours] = useState(null);
  const [contactInfo, setContactInfo] = useState({ phone: '+47 466 77 879', email: 'sydniejovel@yahoo.com', address: 'Nordveien 10, 9180 Skjervøy, Norge' });
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [menuEnabled, setMenuEnabled] = useState({ today: true, later: true, soon: true });
  const [specialMenu, setSpecialMenu] = useState(null);
  const [vippsNumber, setVippsNumber] = useState('46677879');

  useEffect(() => {
    axios.get(`${API}/settings/hours`).then(res => setOpeningHours(res.data.hours)).catch(() => {});
    axios.get(`${API}/settings/contact`).then(res => {
      setContactInfo({
        phone: res.data.phone || '+47 466 77 879',
        email: res.data.email || 'sydniejovel@yahoo.com',
        address: res.data.address || 'Nordveien 10, 9180 Skjervøy, Norge'
      });
      setVippsNumber(res.data.vipps_number || '46677879');
    }).catch(() => {});
    // Fetch all menu statuses
    Promise.all([
      axios.get(`${API}/settings/today-menu`),
      axios.get(`${API}/settings/later-menu`),
      axios.get(`${API}/settings/soon-menu`),
      axios.get(`${API}/settings/special-menu`)
    ]).then(([todayRes, laterRes, soonRes, specialRes]) => {
      setMenuEnabled({
        today: todayRes.data.enabled,
        later: laterRes.data.enabled,
        soon: soonRes.data.enabled
      });
      setSpecialMenu(specialRes.data.special_menu || null);
    }).catch(() => {});
  }, []);

  const formatHours = (hours) => {
    if (!hours) return 'Laster...';
    const dayNames = { monday: 'Man', tuesday: 'Tir', wednesday: 'Ons', thursday: 'Tor', friday: 'Fre', saturday: 'Lør', sunday: 'Søn' };
    return Object.entries(dayNames).map(([key, name]) => `${name}: ${hours[key] || 'Stengt'}`).join('\n');
  };

  // Check if today menu is available (only controlled by admin toggle now)
  const isTodayAvailable = () => {
    return menuEnabled.today;
  };

  const getTodayDisabledReason = () => {
    if (!menuEnabled.today) return 'I dag meny er deaktivert';
    return '';
  };

  const handleOrderChoice = (orderType) => {
    setShowOrderModal(false);
    navigate(`/menu?type=${orderType}`);
  };

  return (
    <div className="min-h-screen pb-24 md:pb-0" data-testid="home-page">
      {/* Order Type Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-center">Når vil du ha mat?</DialogTitle>
            <DialogDescription className="sr-only">Velg en av menytypene nedenfor for å starte bestilling.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* SKJERVØY DAGA - shown when enabled (always); ordering blocked if not active_now */}
            {specialMenu?.enabled && (
              <button
                onClick={() => handleOrderChoice('special')}
                className="w-full p-6 rounded-2xl border-2 text-left transition-all border-purple-500 bg-purple-50 hover:bg-purple-100 cursor-pointer"
                data-testid="order-special"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🎪</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-purple-700">{specialMenu.label || 'SKJERVØY DAGA'}</h3>
                    <p className="text-sm text-purple-600">
                      {specialMenu.active_now
                        ? `Spesialmeny · ${specialMenu.start_time || '08:00'} – ${specialMenu.end_time || '22:00'}`
                        : `👀 Forhåndsvisning · åpner ${specialMenu.start_date ? new Date(specialMenu.start_date).toLocaleDateString('nb-NO', {day:'numeric',month:'long'}) : 'snart'}`}
                    </p>
                  </div>
                </div>
              </button>
            )}

            {/* I dag - Så fort som mulig */}
            <button
              onClick={() => handleOrderChoice('today')}
              disabled={!isTodayAvailable() || specialMenu?.active_now}
              className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                isTodayAvailable() && !specialMenu?.active_now
                  ? 'border-green-500 bg-green-50 hover:bg-green-100 cursor-pointer' 
                  : 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
              }`}
              data-testid="order-today"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${isTodayAvailable() && !specialMenu?.active_now ? 'bg-green-500' : 'bg-gray-400'} rounded-full flex items-center justify-center`}>
                  <span className="text-2xl">⚡</span>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isTodayAvailable() && !specialMenu?.active_now ? 'text-green-700' : 'text-gray-500'}`}>I dag</h3>
                  <p className={`text-sm ${isTodayAvailable() && !specialMenu?.active_now ? 'text-green-600' : 'text-gray-400'}`}>Så fort som mulig</p>
                  {specialMenu?.active_now ? (
                    <p className="text-xs text-purple-600 mt-1">Stengt – {specialMenu.label || 'SKJERVØY DAGA'} pågår</p>
                  ) : (
                    !isTodayAvailable() && <p className="text-xs text-red-500 mt-1">{getTodayDisabledReason()}</p>
                  )}
                </div>
              </div>
            </button>

            {/* Senere - 5 dager frem */}
            <button
              onClick={() => handleOrderChoice('later')}
              disabled={!menuEnabled.later}
              className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                menuEnabled.later 
                  ? 'border-yellow-500 bg-yellow-50 hover:bg-yellow-100 cursor-pointer' 
                  : 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
              }`}
              data-testid="order-later"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${menuEnabled.later ? 'bg-yellow-500' : 'bg-gray-400'} rounded-full flex items-center justify-center`}>
                  <span className="text-2xl">📅</span>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${menuEnabled.later ? 'text-yellow-700' : 'text-gray-500'}`}>Senere</h3>
                  <p className={`text-sm ${menuEnabled.later ? 'text-yellow-600' : 'text-gray-400'}`}>Velg dato og tid (innen 3 dager)</p>
                  {!menuEnabled.later && <p className="text-xs text-red-500 mt-1">Senere meny er deaktivert</p>}
                </div>
              </div>
            </button>

            {/* Snart - Store bestillinger */}
            <button
              onClick={() => handleOrderChoice('soon')}
              disabled={!menuEnabled.soon}
              className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                menuEnabled.soon 
                  ? 'border-orange-500 bg-orange-50 hover:bg-orange-100 cursor-pointer' 
                  : 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60'
              }`}
              data-testid="order-soon"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${menuEnabled.soon ? 'bg-orange-500' : 'bg-gray-400'} rounded-full flex items-center justify-center`}>
                  <span className="text-2xl">🎉</span>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${menuEnabled.soon ? 'text-orange-700' : 'text-gray-500'}`}>Snart</h3>
                  <p className={`text-sm ${menuEnabled.soon ? 'text-orange-600' : 'text-gray-400'}`}>Store bestillinger (minst 1 uke frem)</p>
                  {!menuEnabled.soon && <p className="text-xs text-red-500 mt-1">Snart meny er deaktivert</p>}
                </div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <section className="relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-24">
          {/* Title above pictures */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-primary leading-none mb-2">Smak av <span className="text-accent">Asiatisk Mat</span></h1>
          <p className="text-sm md:text-base font-bold text-muted-foreground mb-8">Take away mat fra Skjervøy. Hjemmelaget med kjærlighet og lokale råvarer.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(180px,auto)]">
            <div className="md:col-span-7 md:row-span-2 relative rounded-3xl overflow-hidden group">
              <img src="https://customer-assets.emergentagent.com/job_d3d1e2c0-caf9-4b5c-9d48-f23039e8e008/artifacts/e0inopw9_Sydnie%27s%20meny%20%283%29.png" alt="Sydnie's Meny" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 md:p-12">
                <Button onClick={() => setShowOrderModal(true)} className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg" data-testid="hero-order-button">
                  Bestill nå<ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            <div className="md:col-span-5 relative rounded-3xl overflow-hidden group">
              <img src="https://customer-assets.emergentagent.com/job_sydnies-meny-1/artifacts/pi0j5q1h_pancit-bihon-philippine-fried-rice-noodles-traditional-filipino-meal-160950154.jpg" alt="Pansit" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6"><span className="font-accent text-accent text-xl">Favoritt</span><h3 className="text-2xl font-serif font-semibold text-white">Pansit</h3></div>
            </div>
            <div className="md:col-span-5 relative rounded-3xl overflow-hidden group">
              <img src="https://customer-assets.emergentagent.com/job_26f5801e-9826-4cb7-a7e8-0efd6be164e9/artifacts/l5zmkjqn_Spring-Rolls-6.jpg" alt="Vårruller" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6"><span className="font-accent text-accent text-xl">Bestselger</span><h3 className="text-2xl font-serif font-semibold text-white">Vårruller</h3></div>
            </div>
          </div>
          
          {/* Fried Chicken Feature */}
          <div className="mt-6">
            <div className="relative rounded-3xl overflow-hidden group h-[200px]">
              <img src="https://customer-assets.emergentagent.com/job_26f5801e-9826-4cb7-a7e8-0efd6be164e9/artifacts/i09h7nlk_Sydnie%20Meny.png" alt="Stekt Kylling og Chips" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6"><span className="font-accent text-accent text-xl">Populær</span><h3 className="text-2xl font-serif font-semibold text-white">Stekt Kylling og Chips</h3></div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 border border-border/50">
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-secondary/10 rounded-2xl flex items-center justify-center mb-6"><MapPin className="w-7 h-7 text-secondary" /></div>
                <h3 className="text-xl font-serif font-semibold mb-2">Finn oss</h3>
                <p className="text-muted-foreground">{contactInfo.address.split(',')[0]}<br />{contactInfo.address.split(',').slice(1).join(',').trim()}</p>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 border border-border/50">
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-accent/20 rounded-2xl flex items-center justify-center mb-6"><Clock className="w-7 h-7 text-accent" /></div>
                <h3 className="text-xl font-serif font-semibold mb-2">Åpningstider</h3>
                <p className="text-muted-foreground whitespace-pre-line text-sm">{openingHours ? formatHours(openingHours) : 'Laster...'}</p>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 border border-border/50">
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-6"><CreditCard className="w-7 h-7 text-green-600" /></div>
                <h3 className="text-xl font-serif font-semibold mb-2">Betaling</h3>
                <p className="text-muted-foreground mb-3">Kontant, Vipps og Wise App</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <img src="https://customer-assets.emergentagent.com/job_dine-dash-60/artifacts/xq4v88zz_nok%20logo.webp" alt="Kontant" className="h-6 w-6 object-contain" />
                    <span className="text-sm font-medium text-gray-600">Kontant</span>
                  </div>
                  <a href="https://play.google.com/store/apps/details?id=no.dnb.vipps" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-orange-50 px-3 py-2 rounded-lg hover:bg-orange-100 transition-colors">
                    <img src="https://customer-assets.emergentagent.com/job_dine-dash-60/artifacts/bir8bpst_vipps.webp" alt="Vipps" className="h-6 w-6 rounded object-cover" />
                    <span className="text-sm font-medium text-orange-600">{vippsNumber}</span>
                  </a>
                  <a href="https://wise.com/pay/business/sydniesmeny" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg hover:bg-green-100 transition-colors">
                    <img src="https://customer-assets.emergentagent.com/job_dine-dash-60/artifacts/kwwi39oj_Wise.png" alt="Wise" className="h-6 w-6 rounded object-cover" />
                    <span className="text-sm font-medium text-green-600">Betal</span>
                  </a>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-all duration-300 border border-border/50">
              <CardContent className="p-0">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6"><Phone className="w-7 h-7 text-primary" /></div>
                <h3 className="text-xl font-serif font-semibold mb-2">Kontakt</h3>
                <p className="text-muted-foreground">{contactInfo.phone}<br />{contactInfo.email}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6">Klar til å bestille?</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10">Bestill online og betal ved henting eller levering. Enkelt og raskt!</p>
          <Button onClick={() => setShowOrderModal(true)} className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-10 py-6 text-lg" data-testid="cta-order-button">Bestill nå<ChevronRight className="w-5 h-5 ml-2" /></Button>
        </div>
      </section>
    </div>
  );
};

// Menu Page
const MenuPage = () => {
  const [menu, setMenu] = useState({ categories: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const { addToCart } = useCart();
  const { user, token, updateFavorites } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get menu type from URL
  const menuType = searchParams.get('type') || 'today';
  
  // State for date/time picker modal (for "later" and "soon" menus)
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [openingHours, setOpeningHours] = useState(null);
  const [specialMenu, setSpecialMenu] = useState(null);

  // Menu type labels
  const menuTypeLabels = {
    today: { title: 'Meny - I dag', subtitle: 'Så fort som mulig', color: 'bg-green-500' },
    later: { title: 'Meny - Senere', subtitle: 'Velg dato og tid (innen 3 dager)', color: 'bg-yellow-500' },
    soon: { title: 'Meny - Store Bestillinger', subtitle: 'Velg dato (minst 1 uke frem)', color: 'bg-orange-500' },
    special: { title: `Meny - ${specialMenu?.label || 'SKJERVØY DAGA'}`, subtitle: `Spesialmeny · ${specialMenu?.start_time || '08:00'} – ${specialMenu?.end_time || '22:00'}`, color: 'bg-purple-500' }
  };

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await axios.get(`${API}/menu/type/${menuType}`);
        if (res.data.items?.length > 0) {
          setMenu(res.data);
          // Set active category based on order
          const allCategories = Object.keys(res.data.categories);
          const sortedCategories = allCategories.sort((a, b) => a.localeCompare(b, 'nb', { numeric: true }));
          setActiveCategory(sortedCategories[0]);
        } else {
          // If no items, show empty state
          setMenu({ categories: {}, items: [] });
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchMenu();
    
    // Fetch opening hours for time slot generation
    axios.get(`${API}/settings/hours`).then(res => setOpeningHours(res.data.hours)).catch(() => {});
    // Fetch special menu config
    axios.get(`${API}/settings/special-menu`).then(res => setSpecialMenu(res.data.special_menu)).catch(() => {});
  }, [menuType]);

  const toggleFavorite = async (itemId) => {
    if (!token) { toast.error('Logg inn for å lagre favoritter'); return; }
    try {
      const isFav = user?.favorites?.includes(itemId);
      if (isFav) {
        await axios.delete(`${API}/favorites/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
        updateFavorites(user.favorites.filter(f => f !== itemId));
        toast.success('Fjernet fra favoritter');
      } else {
        await axios.post(`${API}/favorites/${itemId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        updateFavorites([...(user.favorites || []), itemId]);
        toast.success('Lagt til i favoritter');
      }
    } catch (e) { toast.error('Kunne ikke oppdatere favoritter'); }
  };

  // Generate available dates based on menu type
  const getAvailableDates = () => {
    const dates = [];
    const now = new Date();
    
    if (menuType === 'later') {
      // 3 days ahead
      for (let i = 1; i <= 3; i++) {
        const date = new Date(now);
        date.setDate(date.getDate() + i);
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'short' })
        });
      }
    } else if (menuType === 'soon') {
      // 1 week to 4 weeks ahead
      for (let i = 7; i <= 28; i++) {
        const date = new Date(now);
        date.setDate(date.getDate() + i);
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'short' })
        });
      }
    }
    return dates;
  };

  // Generate time slots for selected date
  const getTimeSlots = () => {
    if (!selectedDate || !openingHours) return [];
    const date = new Date(selectedDate);
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = days[date.getDay()];
    const dayHours = openingHours[dayName];
    
    if (!dayHours || dayHours.toLowerCase() === 'stengt') return [];
    
    const match = dayHours.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
    if (!match) return [];
    
    const slots = [];
    const openHour = parseInt(match[1]);
    const openMin = parseInt(match[2]);
    const closeHour = parseInt(match[3]);
    const closeMin = parseInt(match[4]);
    
    // Generate half-hour slots
    let totalMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;
    while (totalMinutes <= closeMinutes) {
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      totalMinutes += 30;
    }
    return slots;
  };

  // Handle add to cart - NO date/time picker for any menu type
  const handleAddToCart = (item) => {
    // Block ordering when special menu is in preview mode (enabled but not active_now)
    if (menuType === 'special' && specialMenu && specialMenu.enabled && !specialMenu.active_now) {
      const startLabel = specialMenu.start_date 
        ? new Date(specialMenu.start_date).toLocaleDateString('nb-NO', {day:'numeric', month:'long'}) 
        : 'snart';
      toast.error(`Kan ikke bestille ennå. ${specialMenu.label || 'SKJERVØY DAGA'} åpner ${startLabel}.`);
      return;
    }
    const salePrice = getActiveSale(item);
    const effectivePrice = salePrice ?? item.price;
    const added = addToCart({...item, price: effectivePrice, original_price: item.price, on_sale: salePrice !== null, menuType: menuType});
    if (added) {
      toast.success(`${item.name} lagt til i handlekurven`);
    }
  };

  // Confirm add with date/time
  const confirmAddToCart = () => {
    if (!selectedDate) {
      toast.error('Velg en dato');
      return;
    }
    if (menuType === 'later' && !selectedTime) {
      toast.error('Velg et tidspunkt');
      return;
    }
    
    // Add to cart with scheduled date/time
    const cartItem = {
      ...selectedItem,
      scheduled_date: selectedDate,
      scheduled_time: menuType === 'later' ? selectedTime : null
    };
    addToCart(cartItem);
    setShowDatePicker(false);
    
    const dateLabel = new Date(selectedDate).toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'short' });
    const timeLabel = selectedTime ? ` kl ${selectedTime}` : '';
    toast.success(`${selectedItem.name} lagt til for ${dateLabel}${timeLabel}`);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  const currentMenuInfo = menuTypeLabels[menuType];

  return (
    <div className="min-h-screen pb-24 md:pb-12" data-testid="menu-page">
      <section className={`${currentMenuInfo.color} text-white py-12 md:py-16`}>
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <button onClick={() => navigate('/')} className="text-white/70 hover:text-white mb-4 flex items-center gap-2">
            ← Tilbake
          </button>
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-2">{currentMenuInfo.title}</h1>
          <p className="text-lg text-white/70">{currentMenuInfo.subtitle}</p>
        </div>
      </section>

      {/* Preview-mode banner for SKJERVØY DAGA */}
      {menuType === 'special' && specialMenu && specialMenu.enabled && !specialMenu.active_now && (
        <div className="bg-purple-100 border-b-2 border-purple-300 py-3" data-testid="special-preview-banner">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <p className="text-sm md:text-base font-semibold text-purple-800">
              👀 Forhåndsvisning – Bestilling åpner {specialMenu.start_date ? new Date(specialMenu.start_date).toLocaleDateString('nb-NO', {weekday:'long', day:'numeric', month:'long'}) : 'snart'}
            </p>
            <p className="text-xs text-purple-700 mt-1">Du kan se menyen nå, men knappene blir aktive først på åpningsdagen.</p>
          </div>
        </div>
      )}

      {Object.keys(menu.categories).length === 0 ? (
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <UtensilsCrossed className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Ingen retter tilgjengelig</h2>
            <p className="text-muted-foreground mb-6">Det er ingen retter i denne menyen ennå.</p>
            <Button onClick={() => navigate('/')} variant="outline">Tilbake til forsiden</Button>
          </div>
        </section>
      ) : (
        <>
          <section className="py-8 md:py-12">
            <div className="container mx-auto px-4 md:px-8 lg:px-12">
              {(() => {
                const allCategories = Object.keys(menu.categories);
                // Sort categories naturally (numbered categories like "1. Pansit" sort correctly)
                const sortedCategories = allCategories.sort((a, b) => a.localeCompare(b, 'nb', { numeric: true }));
                
                // Collect all items with active sale
                const saleItems = menu.items.filter(item => getActiveSale(item) !== null);
                
                const renderCard = (item, extraKey = '') => {
                  const salePrice = getActiveSale(item);
                  return (
                    <Card key={`${extraKey}${item.id}`} className={`bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-hover transition-all duration-300 border ${salePrice ? 'border-red-300 ring-2 ring-red-200' : 'border-border/50'} group`} data-testid={`menu-item-${item.id}`}>
                      <div className="relative h-48 overflow-hidden">
                        <img src={getImageUrl(item.image_url) || 'https://customer-assets.emergentagent.com/job_0f7325c6-c25a-4e24-9309-aa313ad01ddd/artifacts/25lil8kr_SYDNIE%27S%20FRIED%20CHICKEN.png'} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                        <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
                          {salePrice ? (
                            <>
                              <Badge className="bg-red-600 text-white font-bold px-3 py-1 text-xs shadow-md animate-pulse" data-testid={`sale-badge-${item.id}`}>🔥 {item.sale_discount_percent ? `-${item.sale_discount_percent}%` : 'TILBUD'}</Badge>
                              <Badge className="bg-accent text-foreground font-bold px-4 py-2 text-lg shadow-md">kr {salePrice}</Badge>
                            </>
                          ) : (
                            <Badge className="bg-accent text-foreground font-bold px-4 py-2 text-lg shadow-md">kr {item.price}</Badge>
                          )}
                        </div>
                        <button onClick={() => toggleFavorite(item.id)} className={`absolute top-4 left-4 w-10 h-10 rounded-full flex items-center justify-center transition-all ${user?.favorites?.includes(item.id) ? 'bg-red-500 text-white' : 'bg-white/80 text-muted-foreground hover:bg-white'}`} data-testid={`favorite-${item.id}`}>
                          <Heart className={`w-5 h-5 ${user?.favorites?.includes(item.id) ? 'fill-current' : ''}`} />
                        </button>
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-xl font-serif font-semibold mb-2">{item.name}</h3>
                        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{item.description}</p>
                        <div className="flex items-center justify-between mb-4">
                          {salePrice ? (
                            <div className="flex items-baseline gap-2">
                              <span className="text-2xl font-bold text-red-600">kr {salePrice}</span>
                              <span className="text-base text-muted-foreground line-through">kr {item.price}</span>
                            </div>
                          ) : (
                            <span className="text-2xl font-bold text-secondary">kr {item.price}</span>
                          )}
                        </div>
                        <Button className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full" onClick={() => handleAddToCart(item)} data-testid={`add-to-cart-${item.id}`}>
                          <Plus className="w-4 h-4 mr-2" />Legg til
                        </Button>
                      </CardContent>
                    </Card>
                  );
                };
                
                return (
                  <>
                    {/* 🔥 Tilbud section - shown at top if any item is on sale */}
                    {saleItems.length > 0 && (
                      <div className="mb-12" data-testid="sale-section">
                        <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-2xl p-6 mb-6 shadow-lg">
                          <h2 className="text-3xl font-serif font-bold mb-2 flex items-center gap-2">🔥 Tilbud</h2>
                          <p className="text-white/90">Spar penger - kun for en begrenset tid!</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                          {saleItems.map(item => renderCard(item, 'sale-'))}
                        </div>
                      </div>
                    )}
                    
                    {sortedCategories.map((category) => (
                      <div key={category} className="mb-12">
                        <h2 className="text-2xl font-serif font-bold mb-6 text-primary border-b-2 border-secondary pb-2">{category}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                          {menu.categories[category]?.map((item) => renderCard(item))}
                        </div>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { cart, total, clearCart, orderType } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [vippsNumber, setVippsNumber] = useState('46677879');
  const [deliveryFee, setDeliveryFee] = useState(35);
  const [minDeliveryAmount, setMinDeliveryAmount] = useState(200);
  const [paymentMethod, setPaymentMethod] = useState('vipps');
  const [isNorwegianPhone, setIsNorwegianPhone] = useState(true);
  const [showNonNorwegianDialog, setShowNonNorwegianDialog] = useState(false);
  
  // Calculate default date based on orderType
  const getDefaultDate = () => {
    const now = new Date();
    if (orderType === 'soon') {
      // For "Snart" orders, start at least 1 week ahead
      now.setDate(now.getDate() + 7);
    }
    return now.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_phone: '',
    delivery_address: '',
    pickup: true,
    notes: '',
    scheduled_date: getDefaultDate(),
    scheduled_hour: ''
  });

  const [availableTimes, setAvailableTimes] = useState([]);
  const [daysAhead, setDaysAhead] = useState(7);
  const [laterDays, setLaterDays] = useState(5);
  const [soonDays, setSoonDays] = useState(30);
  const [closedDates, setClosedDates] = useState([]);
  const [slotCounts, setSlotCounts] = useState({});
  const [openingHours, setOpeningHours] = useState(null);
  const [maxBookingsPerSlot, setMaxBookingsPerSlot] = useState(2);
  const [asapEnabled, setAsapEnabled] = useState(true);
  const [disabledSlots, setDisabledSlots] = useState([]);
  const [orderSlots, setOrderSlots] = useState({ today: [], later: [], soon: [], special: [] });
  const [specialMenu, setSpecialMenu] = useState(null);
  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = formData.scheduled_date === todayStr;

  // Get day name for selected date
  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  // Parse opening hours string like "14:00 - 20:00" to get end time
  const parseClosingTime = (hoursStr) => {
    if (!hoursStr || hoursStr.toLowerCase() === 'stengt') return null;
    const match = hoursStr.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
    if (match) {
      return { 
        openHour: parseInt(match[1]), 
        openMinute: parseInt(match[2]),
        closeHour: parseInt(match[3]), 
        closeMinute: parseInt(match[4]) 
      };
    }
    return null;
  };

  // Generate hourly time slots based on opening hours
  const generateTimeSlots = () => {
    if (!formData.scheduled_date) return [];
    
    // SPECIAL menu (Skjervøy Daga) uses its own time range
    if (orderType === 'special' && specialMenu) {
      const parseTime = (t) => {
        const m = String(t || '').match(/^(\d{1,2}):(\d{2})$/);
        return m ? { h: parseInt(m[1]), m: parseInt(m[2]) } : null;
      };
      const start = parseTime(specialMenu.start_time) || { h: 8, m: 0 };
      const end = parseTime(specialMenu.end_time) || { h: 22, m: 0 };
      const slots = [];
      let total = start.h * 60 + start.m;
      const endTotal = end.h * 60 + end.m;
      while (total <= endTotal) {
        const h = Math.floor(total / 60);
        const m = total % 60;
        slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
        total += 30;
      }
      return slots;
    }
    
    const dayName = getDayName(formData.scheduled_date);
    const dayHours = openingHours ? openingHours[dayName] : null;
    const times = parseClosingTime(dayHours);
    
    // For Senere/Snart on "Stengt" weekdays, use default hours (14:00 - 19:00)
    // For "I dag" and other valid days, respect the set hours
    if (!times) {
      if (orderType === 'later' || orderType === 'soon') {
        const defaultSlots = [];
        for (let hour = 14; hour <= 19; hour++) {
          defaultSlots.push(`${hour.toString().padStart(2, '0')}:00`);
          if (hour < 19) defaultSlots.push(`${hour.toString().padStart(2, '0')}:30`);
        }
        return defaultSlots;
      }
      // For "I dag" - use 13-18 default
      const defaultSlots = [];
      for (let hour = 13; hour <= 18; hour++) {
        defaultSlots.push(`${hour.toString().padStart(2, '0')}:00`);
        if (hour < 18) defaultSlots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
      return defaultSlots;
    }
    
    const slots = [];
    // Generate half-hour slots from open to close time
    let totalMinutes = times.openHour * 60 + (times.openMinute || 0);
    const closeMinutes = times.closeHour * 60 + (times.closeMinute || 0);
    while (totalMinutes <= closeMinutes) {
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      totalMinutes += 30;
    }
    
    return slots;
  };

  // Use order slots from admin if configured, else fall back to generated slots
  const allTimeSlots = (() => {
    const adminSlots = orderSlots[orderType] || [];
    if (adminSlots.length > 0) {
      // Use admin-configured slots, filtered by pickup/delivery
      return adminSlots
        .filter(s => s.available)
        .filter(s => formData.pickup ? true : s.delivery_available)
        .map(s => s.time);
    }
    // Legacy fallback - generated slots minus disabled
    return generateTimeSlots().filter(t => !disabledSlots.includes(t));
  })();

  // Filter times for today - need 30 min prep + skip current half-hour slot
  const getAvailableTimesForDate = () => {
    if (!isToday) return allTimeSlots;
    
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    // Round UP to next 30-minute mark, then add 30 more minutes for prep time
    // Examples: at 15:01 → next slot = 16:00, at 15:30 → next slot = 16:30
    const nextHalfHour = Math.ceil((currentMinutes + 1) / 30) * 30;
    const minSlot = nextHalfHour + 30;
    
    return allTimeSlots.filter(time => {
      const [h, m] = time.split(':').map(Number);
      const slotMinutes = h * 60 + m;
      return slotMinutes >= minSlot;
    });
  };

  const timeSlotsForDate = getAvailableTimesForDate();

  // Fetch slot counts when date changes
  useEffect(() => {
    if (formData.scheduled_date) {
      axios.get(`${API}/timeslots/${formData.scheduled_date}`)
        .then(res => setSlotCounts(res.data.slot_counts || {}))
        .catch(() => setSlotCounts({}));
    }
  }, [formData.scheduled_date]);

  // Get day name for a date string
  const getDayNameForDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  // Check if a day is open based on opening hours
  const isDayOpen = (dateStr) => {
    if (!openingHours) return true; // Default to open if no hours loaded
    const dayName = getDayNameForDate(dateStr);
    const dayHours = openingHours[dayName];
    return dayHours && dayHours.toLowerCase() !== 'stengt';
  };

  // Generate dates based on orderType and daysAhead setting
  const availableDates = useMemo(() => {
    const dates = [];
    const now = new Date();
    
    // Determine start day and max days based on orderType
    let startDay = 0;
    let maxDays = daysAhead;
    
    if (orderType === 'today' || orderType === 'special') {
      // Only today
      startDay = 0;
      maxDays = 1;
    } else if (orderType === 'later') {
      // Start from tomorrow, search enough days to find laterDays open days
      startDay = 1;
      maxDays = laterDays * 3; // Search wider range to find enough open days
    } else if (orderType === 'soon') {
      // At least 1 week ahead, up to soonDays
      startDay = 7;
      maxDays = Math.max(soonDays, 30);
    }
    
    for (let i = startDay; i < maxDays; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip manually closed dates (specific dates user marked as closed)
      if (closedDates.includes(dateStr)) continue;
      
      // For "I dag" orders, respect weekday "Stengt" setting
      // For "Senere", "Snart", "Special", always show all days (ignore weekday Stengt)
      if (orderType === 'today' && !isDayOpen(dateStr)) continue;
      
      // Generate label
      let label;
      if (i === 0) label = 'I dag';
      else label = date.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'short' });
      
      dates.push({ value: dateStr, label });
      
      // For "Senere" orders, limit to laterDays open days
      if (orderType === 'later' && dates.length >= laterDays) break;
    }
    return dates;
  }, [daysAhead, closedDates, openingHours, orderType, laterDays, soonDays]);

  // Auto-select first available date if current selection is not available
  useEffect(() => {
    if (availableDates.length > 0 && !availableDates.find(d => d.value === formData.scheduled_date)) {
      setFormData(prev => ({...prev, scheduled_date: availableDates[0].value}));
    }
  }, [availableDates, formData.scheduled_date]);

  // Reset time when date changes - require user to explicitly choose
  useEffect(() => {
    setFormData(prev => ({...prev, scheduled_hour: ''}));
  }, [formData.scheduled_date, orderType]);

  useEffect(() => {
    if (user) setFormData(prev => ({ ...prev, customer_name: user.name || '' }));
  }, [user]);

  useEffect(() => {
    // Check if delivery is available based on orderType
    const fetchDeliveryStatus = async () => {
      try {
        if (orderType === 'today' || orderType === 'special') {
          // For "I dag" / "Skjervøy Daga", use global delivery setting
          const res = await axios.get(`${API}/restaurant`);
          setDeliveryEnabled(res.data.delivery_enabled);
          if (!res.data.delivery_enabled) {
            setFormData(prev => ({ ...prev, pickup: true }));
          }
        } else if (orderType === 'later') {
          // For "Senere", check later-delivery setting
          const res = await axios.get(`${API}/settings/later-delivery`);
          setDeliveryEnabled(res.data.enabled);
          if (!res.data.enabled) {
            setFormData(prev => ({ ...prev, pickup: true }));
          }
        } else if (orderType === 'soon') {
          // For "Snart", check soon-delivery setting
          const res = await axios.get(`${API}/settings/soon-delivery`);
          setDeliveryEnabled(res.data.enabled);
          if (!res.data.enabled) {
            setFormData(prev => ({ ...prev, pickup: true }));
          }
        }
      } catch (e) {
        // Fallback to global setting
        try {
          const res = await axios.get(`${API}/restaurant`);
          setDeliveryEnabled(res.data.delivery_enabled);
        } catch (err) {}
      }
    };
    
    fetchDeliveryStatus();
    
    axios.get(`${API}/settings/contact`).then(res => {
      setVippsNumber(res.data.vipps_number || '46677879');
      setDeliveryFee(res.data.delivery_fee || 35);
      setMinDeliveryAmount(res.data.min_delivery_amount || 200);
      setAvailableTimes(res.data.available_times || ['10:00', '13:00', '16:00']);
      setDaysAhead(res.data.days_ahead || 7);
      setClosedDates(res.data.closed_dates || []);
    }).catch(() => {});
    // Fetch opening hours
    axios.get(`${API}/settings/hours`).then(res => {
      setOpeningHours(res.data.hours);
    }).catch(() => {});
    // Fetch later days setting
    axios.get(`${API}/settings/later-days`).then(res => {
      setLaterDays(res.data.later_days || 5);
    }).catch(() => {});
    // Fetch soon days setting
    axios.get(`${API}/settings/soon-days`).then(res => {
      setSoonDays(res.data.soon_days || 30);
    }).catch(() => {});
    // Fetch ASAP enabled
    axios.get(`${API}/settings/asap-enabled`).then(res => {
      setAsapEnabled(res.data.enabled !== false);
    }).catch(() => {});
    // Fetch max bookings per slot
    axios.get(`${API}/settings/max-bookings`).then(res => {
      setMaxBookingsPerSlot(res.data.max_bookings || 2);
    }).catch(() => {});
    // Fetch disabled time slots
    axios.get(`${API}/settings/disabled-slots`).then(res => {
      setDisabledSlots(res.data.disabled_slots || []);
    }).catch(() => {});
    // Fetch order slots (per menu type with pickup/delivery toggle)
    axios.get(`${API}/settings/order-slots`).then(res => {
      const s = res.data.order_slots || {};
      setOrderSlots({
        today: s.today || [],
        later: s.later || [],
        soon: s.soon || [],
        special: s.special || []
      });
    }).catch(() => {});
    // Fetch special menu config (for SKJERVØY DAGA time range)
    axios.get(`${API}/settings/special-menu`).then(res => {
      setSpecialMenu(res.data.special_menu || null);
    }).catch(() => {});
  }, [orderType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { toast.error("Handlekurven er tom"); return; }
    
    // Validate based on orderType - strict: require explicit time/date choice
    if ((orderType === 'today' || orderType === 'special') && !formData.scheduled_hour) { 
      toast.error("Velg når du vil ha maten");
      return; 
    }
    if (orderType === 'later' && (!formData.scheduled_date || !formData.scheduled_hour)) {
      toast.error("Velg dato og tid for bestillingen");
      return;
    }
    if (orderType === 'soon' && (!formData.scheduled_date || !formData.scheduled_hour)) {
      toast.error("Velg dato og tid for bestillingen");
      return;
    }
    
    // Validate Norwegian phone number - normalize to +47XXXXXXXX
    const rawPhone = (formData.customer_phone || '').replace(/[\s\-()]/g, '');
    let normalizedPhone = '';
    if (/^\+47\d{8}$/.test(rawPhone)) {
      normalizedPhone = rawPhone;
    } else if (/^47\d{8}$/.test(rawPhone)) {
      normalizedPhone = `+${rawPhone}`;
    } else if (/^\d{8}$/.test(rawPhone)) {
      normalizedPhone = `+47${rawPhone}`;
    } else if (rawPhone.length >= 6) {
      // Non-Norwegian number - require login to track orders in-app
      if (!user) {
        setShowNonNorwegianDialog(true);
        return;
      }
      // Logged-in user with non-Norwegian number - allow, no SMS
      normalizedPhone = rawPhone.startsWith('+') ? rawPhone : `+${rawPhone}`;
    } else {
      toast.error("Skriv inn et gyldig telefonnummer");
      return;
    }
    
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Determine scheduled time based on orderType
      let scheduledDateTime = null;
      
      if (orderType === 'today' || orderType === 'special') {
        // For "I dag" or "Skjervøy Daga" orders
        const today = new Date().toISOString().split('T')[0];
        scheduledDateTime = formData.scheduled_hour === 'asap' 
          ? null 
          : `${today}T${formData.scheduled_hour}:00`;
      } else if (orderType === 'later') {
        // For "Senere" orders - date + time
        scheduledDateTime = `${formData.scheduled_date}T${formData.scheduled_hour}:00`;
      } else if (orderType === 'soon') {
        // For "Snart" orders - only date (no specific time)
        scheduledDateTime = formData.scheduled_date;
      }
      
      const orderData = {
        customer_name: formData.customer_name,
        customer_phone: normalizedPhone,
        delivery_address: formData.delivery_address,
        pickup: formData.pickup,
        notes: formData.notes,
        items: cart,
        scheduled_time: scheduledDateTime,
        order_type: orderType,
        payment_method: paymentMethod
      };
      const orderRes = await axios.post(`${API}/orders`, orderData, { headers });
      clearCart();
      navigate(`/order-confirmation?order_id=${orderRes.data.id}`);
    } catch (e) {
      toast.error("Kunne ikke opprette bestilling. Prøv igjen.");
      setLoading(false);
    }
  };

  const finalTotal = formData.pickup ? total : total + deliveryFee;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4" data-testid="checkout-empty">
        <ShoppingCart className="w-20 h-20 text-muted-foreground/30 mb-6" />
        <h2 className="text-2xl font-serif font-semibold mb-4">Handlekurven er tom</h2>
        <p className="text-muted-foreground mb-8">Legg til noen lekre retter fra menyen</p>
        <Button onClick={() => navigate('/menu')} className="bg-secondary hover:bg-secondary/90 text-white rounded-full px-8" data-testid="go-to-menu-button">Se menyen</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-12" data-testid="checkout-page">
      <section className="bg-primary text-white py-12"><div className="container mx-auto px-4 md:px-8 lg:px-12"><h1 className="text-3xl md:text-4xl font-serif font-bold">Kassen</h1></div></section>
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="text-2xl font-serif font-semibold mb-6">Din bestilling</h2>
              <Card className="bg-white rounded-2xl p-6 shadow-soft border border-border/50">
                <div className="space-y-4">
                  {cart.map(item => (
                    <div key={item.menu_item_id} className="flex justify-between items-center py-3 border-b border-border/50 last:border-0">
                      <div><h4 className="font-medium">{item.name}</h4><p className="text-sm text-muted-foreground">Antall: {item.quantity}</p></div>
                      <span className="font-semibold text-secondary">kr {item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>
                <Separator className="my-4" />
                <div className="flex justify-between py-2"><span className="text-muted-foreground">Matvarer</span><span>kr {total}</span></div>
                {!formData.pickup && <div className="flex justify-between py-2"><span className="text-muted-foreground">Levering Skjervøy</span><span>kr {deliveryFee}</span></div>}
                <Separator className="my-2" />
                <div className="flex justify-between text-xl font-bold"><span>Total</span><span className="text-secondary">kr {finalTotal}</span></div>
              </Card>
            </div>
            <div>
              <h2 className="text-2xl font-serif font-semibold mb-6">Dine opplysninger</h2>
              <Card className="bg-white rounded-2xl p-6 shadow-soft border border-border/50">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div><label className="block text-sm font-medium mb-2">Navn *</label><Input value={formData.customer_name} onChange={e => setFormData({...formData, customer_name: e.target.value})} placeholder="Ditt fulle navn" required className="rounded-xl" data-testid="input-name" /></div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Telefon *</label>
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => { setIsNorwegianPhone(true); setFormData({...formData, customer_phone: ''}); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isNorwegianPhone ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        data-testid="phone-norwegian-btn"
                      >🇳🇴 Norsk nummer</button>
                      <button
                        type="button"
                        onClick={() => { setIsNorwegianPhone(false); setFormData({...formData, customer_phone: ''}); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!isNorwegianPhone ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        data-testid="phone-other-btn"
                      >🌍 Annet nummer</button>
                    </div>
                    {isNorwegianPhone ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-3 py-2 rounded-xl bg-gray-100 border border-gray-200 text-sm font-medium text-gray-700">🇳🇴 +47</span>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          maxLength={8}
                          value={formData.customer_phone}
                          onChange={e => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                            setFormData({...formData, customer_phone: digits});
                          }}
                          placeholder="40123456"
                          required
                          className="rounded-xl flex-1"
                          data-testid="input-phone"
                        />
                      </div>
                    ) : (
                      <Input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={e => setFormData({...formData, customer_phone: e.target.value})}
                        placeholder="+63 912 345 6789"
                        required
                        className="rounded-xl"
                        data-testid="input-phone-other"
                      />
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {isNorwegianPhone ? 'SMS-varsler sendes kun til norske numre (8 siffer)' : 'Utenlandske numre mottar ikke SMS - logg inn for å følge bestillingen i appen'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button type="button" className={`flex-1 rounded-xl px-4 py-3 font-medium transition-all duration-200 ${formData.pickup ? 'bg-secondary text-white shadow-md' : 'bg-transparent border-2 border-border text-foreground hover:border-secondary'}`} onClick={() => setFormData(prev => ({...prev, pickup: true, delivery_address: ''}))} data-testid="pickup-option">
                      <div>Henting</div>
                      <div className="text-xs font-normal opacity-80">Nordveien 10 Skjervøy</div>
                    </button>
                    {deliveryEnabled && (
                      <button 
                        type="button" 
                        className={`flex-1 rounded-xl px-4 py-3 font-medium transition-all duration-200 ${
                          total < minDeliveryAmount 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-200' 
                            : !formData.pickup 
                              ? 'bg-secondary text-white shadow-md' 
                              : 'bg-transparent border-2 border-border text-foreground hover:border-secondary'
                        }`} 
                        onClick={() => total >= minDeliveryAmount && setFormData(prev => ({...prev, pickup: false}))} 
                        disabled={total < minDeliveryAmount}
                        data-testid="delivery-option"
                      >
                        <div>Levering (+{deliveryFee} kr)</div>
                        {total < minDeliveryAmount && (
                          <div className="text-xs font-normal">Min. {minDeliveryAmount} kr</div>
                        )}
                      </button>
                    )}
                  </div>
                  {!deliveryEnabled && <p className="text-sm text-amber-600 text-center">Levering er ikke tilgjengelig akkurat nå</p>}
                  {deliveryEnabled && total < minDeliveryAmount && (
                    <p className="text-sm text-amber-600 text-center bg-amber-50 p-2 rounded-lg">
                      🚗 Levering krever minimum {minDeliveryAmount} kr. Du mangler {minDeliveryAmount - total} kr.
                    </p>
                  )}
                  {!formData.pickup && (<div><label className="block text-sm font-medium mb-2">Leveringsadresse i Skjervøy *</label><Textarea value={formData.delivery_address} onChange={e => setFormData({...formData, delivery_address: e.target.value})} placeholder="Gate, postnummer, sted" required={!formData.pickup} className="rounded-xl" data-testid="input-address" /></div>)}
                  <div><label className="block text-sm font-medium mb-2">Notater (valgfritt)</label><Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Allergier, spesielle ønsker..." className="rounded-xl" data-testid="input-notes" /></div>
                  
                  {/* Payment Method Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Hvordan vil du betale? *</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('vipps')}
                        className={`p-2 rounded-lg border-2 text-center transition-all flex items-center gap-2 justify-center ${
                          paymentMethod === 'vipps' 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="payment-vipps"
                      >
                        <img src="https://customer-assets.emergentagent.com/job_dine-dash-60/artifacts/bir8bpst_vipps.webp" alt="Vipps" className="w-6 h-6 rounded" />
                        <span className={`text-sm font-medium ${paymentMethod === 'vipps' ? 'text-orange-700' : 'text-gray-700'}`}>Vipps</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('wise')}
                        className={`p-2 rounded-lg border-2 text-center transition-all flex items-center justify-center ${
                          paymentMethod === 'wise' 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="payment-wise"
                      >
                        <img src="https://customer-assets.emergentagent.com/job_d3d1e2c0-caf9-4b5c-9d48-f23039e8e008/artifacts/wgtan63l_images.png" alt="Wise" className="h-8 rounded" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentMethod('kontant')}
                        className={`p-2 rounded-lg border-2 text-center transition-all flex items-center gap-2 justify-center ${
                          paymentMethod === 'kontant' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="payment-kontant"
                      >
                        <span className="text-lg">💵</span>
                        <span className={`text-sm font-medium ${paymentMethod === 'kontant' ? 'text-blue-700' : 'text-gray-700'}`}>Kontant</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Date/Time selection based on orderType */}
                  {(orderType === 'today' || orderType === 'special') && (
                    // For "I dag" or "Skjervøy Daga" orders - show time picker
                    <div data-testid={orderType === 'special' ? 'special-time-selection' : 'today-time-selection'}>
                      {orderType === 'special' && specialMenu && (
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-3">
                          <p className="text-sm text-purple-700 font-medium">🎪 {specialMenu.label || 'SKJERVØY DAGA'} - Velg tid mellom {specialMenu.start_time || '08:00'} og {specialMenu.end_time || '22:00'}</p>
                        </div>
                      )}
                      <label className="block text-sm font-medium mb-2">Når vil du ha maten?</label>
                      <div className="space-y-2">
                        {asapEnabled && orderType === 'today' ? (
                          <button
                            type="button"
                            onClick={() => setFormData({...formData, scheduled_hour: 'asap'})}
                            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                              formData.scheduled_hour === 'asap' 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid="asap-button"
                          >
                            <p className={`font-medium flex items-center gap-2 ${formData.scheduled_hour === 'asap' ? 'text-green-700' : 'text-gray-700'}`}>
                              ⚡ Så fort som mulig
                            </p>
                          </button>
                        ) : null}
                        <Select value={formData.scheduled_hour !== 'asap' ? formData.scheduled_hour : ''} onValueChange={v => setFormData({...formData, scheduled_hour: v})}>
                          <SelectTrigger className={`rounded-xl ${formData.scheduled_hour !== 'asap' && formData.scheduled_hour ? (orderType === 'special' ? 'border-purple-500 bg-purple-50' : 'border-green-500 bg-green-50') : ''}`} data-testid={`${orderType}-time-select`}>
                            <SelectValue placeholder={asapEnabled && orderType === 'today' ? "📅 Eller velg en tid i dag..." : "📅 Velg en tid i dag..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableTimesForDate().map(time => {
                              const count = slotCounts[time] || 0;
                              const isFull = count >= maxBookingsPerSlot;
                              return (
                                <SelectItem key={time} value={time} disabled={isFull}>
                                  kl {time} {isFull && '(fullt)'}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  {orderType === 'later' && (
                    // For "Senere" orders - date picker (up to 3 days) + time picker
                    <div className="space-y-4" data-testid="later-datetime-selection">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                        <p className="text-sm text-yellow-700 font-medium">🟡 Senere bestilling - Velg dato og tid (innen {laterDays} dager)</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Velg dato *</label>
                        <Select 
                          value={formData.scheduled_date} 
                          onValueChange={v => setFormData({...formData, scheduled_date: v, scheduled_hour: ''})}
                        >
                          <SelectTrigger className="rounded-xl" data-testid="later-date-select">
                            <SelectValue placeholder="Velg en dato..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableDates.map(date => (
                              <SelectItem key={date.value} value={date.value}>{date.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Velg tid *</label>
                        <Select 
                          value={formData.scheduled_hour} 
                          onValueChange={v => setFormData({...formData, scheduled_hour: v})}
                        >
                          <SelectTrigger className={`rounded-xl ${formData.scheduled_hour ? 'border-yellow-500 bg-yellow-50' : ''}`} data-testid="later-time-select">
                            <SelectValue placeholder="Velg en tid..." />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlotsForDate.map(time => {
                              const count = slotCounts[time] || 0;
                              const isFull = count >= maxBookingsPerSlot;
                              return (
                                <SelectItem key={time} value={time} disabled={isFull}>
                                  kl {time} {isFull && '(fullt)'}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {formData.scheduled_date && formData.scheduled_hour && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                          <p className="text-sm text-green-700">
                            ✅ Bestilling: {availableDates.find(d => d.value === formData.scheduled_date)?.label} kl {formData.scheduled_hour}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {orderType === 'soon' && (
                    // For "Snart" orders - date picker only (at least 1 week ahead), no time selection
                    <div className="space-y-4" data-testid="soon-date-selection">
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                        <p className="text-sm text-orange-700 font-medium">🟠 Store bestillinger - Velg dato og tid (minst 1 uke frem)</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Velg dato *</label>
                        <Select 
                          value={formData.scheduled_date} 
                          onValueChange={v => setFormData({...formData, scheduled_date: v})}
                        >
                          <SelectTrigger className="rounded-xl" data-testid="soon-date-select">
                            <SelectValue placeholder="Velg en dato..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableDates.map(date => (
                              <SelectItem key={date.value} value={date.value}>{date.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.scheduled_date && (
                        <div>
                          <label className="block text-sm font-medium mb-2">Velg tid *</label>
                          <Select
                            value={formData.scheduled_hour}
                            onValueChange={v => setFormData({...formData, scheduled_hour: v})}
                          >
                            <SelectTrigger className="rounded-xl" data-testid="soon-time-select">
                              <SelectValue placeholder="Velg et tidspunkt..." />
                            </SelectTrigger>
                            <SelectContent>
                              {timeSlotsForDate.map(time => {
                                const count = slotCounts[time] || 0;
                                const isFull = count >= maxBookingsPerSlot;
                                return (
                                  <SelectItem key={time} value={time} disabled={isFull}>
                                    kl {time} {isFull && '(fullt)'}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {formData.scheduled_date && formData.scheduled_hour && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                          <p className="text-sm text-green-700">
                            ✅ Bestilling planlagt: {availableDates.find(d => d.value === formData.scheduled_date)?.label} kl. {formData.scheduled_hour}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Button type="submit" disabled={loading} className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full py-6 text-lg" data-testid="send-order-button">
                    {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sender...</> : <>Send bestilling<CheckCircle2 className="w-5 h-5 ml-2" /></>}
                  </Button>
                  
                  {/* SMS notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs text-blue-900">
                      📱 <strong>Viktig:</strong> Du får SMS-bekreftelse fra avsender <strong>"Sydnie Meny"</strong>. Hvis du ikke ser meldingen i innboksen, sjekk <strong>"Ukjente avsendere"</strong> eller <strong>"Spam"</strong>-mappen i SMS-appen din.
                    </p>
                  </div>
                  
                  <p className="text-xs text-center text-muted-foreground">Du kan betale med kontant, Vipps og Wise app</p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Non-Norwegian phone dialog - recommend login */}
      <Dialog open={showNonNorwegianDialog} onOpenChange={setShowNonNorwegianDialog}>
        <DialogContent className="sm:max-w-md" data-testid="non-norwegian-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif flex items-center gap-2">
              🌍 Utenlandsk nummer oppdaget
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-muted-foreground">
              Vi sender kun SMS-varsler til norske telefonnumre. For å følge bestillingen din, anbefaler vi at du <strong>logger inn eller oppretter en konto</strong> først.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-900">
                ✅ Fordeler ved å logge inn:<br/>
                • Følg bestillingen i appen (Mine bestillinger)<br/>
                • Se alle dine tidligere bestillinger<br/>
                • Lagre favoritter
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button 
                onClick={() => { setShowNonNorwegianDialog(false); navigate('/profile'); }} 
                className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full"
                data-testid="login-recommend-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />Logg inn / Opprett konto
              </Button>
              <Button 
                variant="outline" 
                onClick={() => { 
                  setShowNonNorwegianDialog(false); 
                  setIsNorwegianPhone(true); 
                  setFormData({...formData, customer_phone: ''}); 
                }}
                className="w-full rounded-full"
                data-testid="use-norwegian-btn"
              >
                🇳🇴 Bruk et norsk nummer i stedet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Order Confirmation Page
const OrderConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vippsNumber, setVippsNumber] = useState('46677879');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) { setLoading(false); return; }
      try {
        const [orderRes, contactRes] = await Promise.all([
          axios.get(`${API}/orders/${orderId}`),
          axios.get(`${API}/settings/contact`)
        ]);
        setOrder(orderRes.data);
        setVippsNumber(contactRes.data.vipps_number || '46677879');
        clearCart();
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchOrder();
  }, [orderId, clearCart]);

  if (loading) return <div className="min-h-screen flex flex-col items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-secondary mb-4" /><p className="text-lg text-muted-foreground">Henter bestilling...</p></div>;
  if (!order) return <div className="min-h-screen flex flex-col items-center justify-center p-4"><X className="w-16 h-16 text-destructive mb-4" /><h2 className="text-2xl font-serif font-semibold mb-2">Bestilling ikke funnet</h2><Button onClick={() => navigate('/')} className="rounded-full">Tilbake til forsiden</Button></div>;

  return (
    <div className="min-h-screen pb-24 md:pb-12" data-testid="confirmation-page">
      <section className="bg-green-600 text-white py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <CheckCircle2 className="w-20 h-20 mx-auto mb-6" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-2">Takk for bestillingen!</h1>
          <p className="text-lg text-white/80">Vi har mottatt din bestilling</p>
        </div>
      </section>
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-2xl">
          <Card className="bg-white rounded-2xl p-8 shadow-soft border border-border/50" data-testid="order-details-card">
            <h2 className="text-2xl font-serif font-semibold mb-6">Bestillingsdetaljer</h2>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between"><span className="text-muted-foreground">Bestillings-ID</span><span className="font-medium font-mono text-sm">{order.id}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Navn</span><span className="font-medium">{order.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Telefon</span><span className="font-medium">{order.customer_phone}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="font-medium">{order.pickup ? 'Henting' : 'Levering'}</span></div>
              {!order.pickup && order.delivery_address && <div className="flex justify-between"><span className="text-muted-foreground">Adresse</span><span className="font-medium text-right max-w-[60%]">{order.delivery_address}</span></div>}
            </div>
            <Separator className="my-6" />
            <h3 className="font-semibold mb-4">Bestilte retter</h3>
            <div className="space-y-3 mb-6">{order.items.map((item, i) => <div key={i} className="flex justify-between py-2 border-b border-border/50 last:border-0"><div><span className="font-medium">{item.name}</span><span className="text-muted-foreground text-sm ml-2">x{item.quantity}</span></div><span className="font-medium">kr {item.price * item.quantity}</span></div>)}</div>
            <div className="flex justify-between text-xl font-bold mb-8"><span>Å betale</span><span className="text-secondary">kr {order.total_amount}</span></div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6"><p className="text-sm text-center text-amber-800 font-medium">{order.pickup ? "💵 Du betaler kr " + order.total_amount + " ved henting" : "💵 Du betaler kr " + order.total_amount + " ved levering"}</p><p className="text-sm text-center text-amber-700 mt-2">Du kan betale med kontant, Vipps og Wise app</p></div>
            <div className="bg-muted/50 rounded-xl p-4 mb-3"><p className="text-sm text-center text-muted-foreground">{order.pickup ? "Vi sender deg melding når bestillingen er klar for henting" : "Vi sender deg melding når bestillingen er på vei"}</p></div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-900 text-center">
                📱 SMS-bekreftelse er sendt fra <strong>"Sydnie Meny"</strong>.<br/>
                Hvis du ikke ser den, sjekk <strong>"Ukjente avsendere"</strong> eller <strong>"Spam"</strong>-mappen.
              </p>
            </div>
            <Button onClick={() => navigate('/menu')} className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full py-6" data-testid="order-more-button">Bestill mer</Button>
          </Card>
        </div>
      </section>
    </div>
  );
};

// User Orders Page
const UserOrdersPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = () => {
    if (!token) return;
    axios.get(`${API}/user/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOrders(res.data.orders))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    fetchOrders();
    
    // Auto-refresh every 30 seconds for real-time updates
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [token, navigate]);

  const statusColors = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', preparing: 'bg-purple-100 text-purple-800', ready: 'bg-green-100 text-green-800', delivered: 'bg-gray-100 text-gray-800', cancelled: 'bg-red-100 text-red-800' };
  const statusLabels = { pending: 'Venter', confirmed: 'Bekreftet', preparing: 'Tilberedes', ready: 'Klar til henting', delivered: 'Levert', cancelled: 'Kansellert' };
  const statusSteps = ['pending', 'confirmed', 'preparing', 'ready', 'delivered'];

  const getStatusStep = (status) => {
    if (status === 'cancelled') return -1;
    return statusSteps.indexOf(status);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div className="min-h-screen pb-24 md:pb-12" data-testid="user-orders-page">
      <section className="bg-primary text-white py-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <h1 className="text-3xl md:text-4xl font-serif font-bold">Mine bestillinger</h1>
          <p className="text-white/70 mt-2">Oppdateres automatisk hvert 30. sekund</p>
        </div>
      </section>
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-3xl">
          {orders.length === 0 ? (
            <div className="text-center py-16"><ClipboardList className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h2 className="text-xl font-semibold mb-2">Ingen bestillinger ennå</h2><p className="text-muted-foreground mb-6">Når du bestiller, vil de vises her</p><Button onClick={() => navigate('/menu')} className="bg-secondary hover:bg-secondary/90">Se menyen</Button></div>
          ) : (
            <div className="space-y-6">
              {orders.map((order, idx) => {
                const currentStep = getStatusStep(order.status);
                const isActive = !['delivered', 'cancelled'].includes(order.status);
                
                return (
                  <Card key={`${order.id}-${idx}`} className={`bg-white rounded-2xl p-6 shadow-soft border ${isActive ? 'border-secondary/50 ring-2 ring-secondary/20' : 'border-border/50'}`} data-testid={`order-${order.id}`}>
                    {/* Active order indicator */}
                    {isActive && (
                      <div className="flex items-center gap-2 mb-4 text-secondary">
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-secondary"></span>
                        </span>
                        <span className="text-sm font-medium">Aktiv bestilling</span>
                      </div>
                    )}
                    
                    {/* Order header */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-mono text-xs text-muted-foreground">#{order.id.slice(-8).toUpperCase()}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <Badge className={`${statusColors[order.status]} text-sm px-3 py-1`}>{statusLabels[order.status]}</Badge>
                    </div>
                    
                    {/* Progress bar for active orders */}
                    {isActive && currentStep >= 0 && (
                      <div className="mb-6">
                        <div className="flex justify-between mb-2">
                          {statusSteps.slice(0, 4).map((step, i) => (
                            <div key={step} className={`text-xs ${i <= currentStep ? 'text-secondary font-medium' : 'text-muted-foreground'}`}>
                              {i === 0 ? 'Mottatt' : i === 1 ? 'Bekreftet' : i === 2 ? 'Lager' : 'Klar'}
                            </div>
                          ))}
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-secondary transition-all duration-500 rounded-full"
                            style={{ width: `${Math.min((currentStep + 1) / 4 * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {/* Order items */}
                    <div className="space-y-1 mb-4">
                      {order.items.map((item, i) => (
                        <p key={i} className="text-sm">{item.name} x{item.quantity} - kr {item.price * item.quantity}</p>
                      ))}
                    </div>
                    
                    {/* Order footer */}
                    <div className="flex justify-between items-center pt-4 border-t border-border/50">
                      <span className="font-semibold text-lg">Total: kr {order.total_amount}</span>
                      <span className="text-sm text-muted-foreground">{order.pickup ? '📍 Henting' : '🚗 Levering'}</span>
                    </div>
                    
                    {/* Scheduled time if any */}
                    {order.scheduled_time && (
                      <div className="mt-3 text-sm text-muted-foreground bg-gray-50 rounded-lg p-3">
                        ⏰ Ønsket tid: {new Date(order.scheduled_time).toLocaleString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Profile Page
const ProfilePage = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setFavorites(res.data.items))
      .catch(console.error);
  }, [token, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen pb-24 md:pb-12" data-testid="profile-page">
      <section className="bg-primary text-white py-12"><div className="container mx-auto px-4 md:px-8 lg:px-12"><h1 className="text-3xl md:text-4xl font-serif font-bold">Min profil</h1></div></section>
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-2xl">
          <Card className="bg-white rounded-2xl p-6 shadow-soft border border-border/50 mb-6">
            <div className="flex items-center gap-4 mb-6"><div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center"><User className="w-8 h-8 text-secondary" /></div><div><h2 className="text-xl font-semibold">{user.name}</h2><p className="text-muted-foreground">{user.email}</p></div></div>
            <div className="space-y-3"><div className="flex justify-between py-2 border-b border-border/50"><span className="text-muted-foreground">Telefon</span><span>{user.phone}</span></div></div>
            <Button variant="outline" className="w-full mt-6" onClick={() => { logout(); navigate('/'); }}><LogOut className="w-4 h-4 mr-2" />Logg ut</Button>
          </Card>
          {favorites.length > 0 && (
            <Card className="bg-white rounded-2xl p-6 shadow-soft border border-border/50">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Heart className="w-5 h-5 text-red-500" />Mine favoritter</h3>
              <div className="space-y-3">{favorites.map(item => <div key={item.id} className="flex justify-between items-center py-2 border-b border-border/50 last:border-0"><span>{item.name}</span><span className="text-secondary font-medium">kr {item.price}</span></div>)}</div>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error('Navn og melding er påkrevd');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      const successMsg = form.email 
        ? 'Melding sendt! Vi har sendt deg en bekreftelse på e-post.' 
        : (res.data.message || 'Melding sendt!');
      toast.success(successMsg);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke sende melding');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-24 md:pb-12" data-testid="contact-page">
      <section className="bg-primary text-white py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4">Kontakt oss</h1>
          <p className="text-lg text-white/70">Vi hører gjerne fra deg - send oss en melding!</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-4 lg:col-span-1">
              <Card className="bg-white rounded-2xl p-6 shadow-soft border border-border/50">
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-accent/20 rounded-2xl flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="text-lg font-serif font-semibold mb-2">Ring oss</h3>
                  <a href="tel:+4746678586" className="text-secondary hover:underline font-medium" data-testid="contact-phone">+47 466 78 586</a>
                </CardContent>
              </Card>
              <Card className="bg-white rounded-2xl p-6 shadow-soft border border-border/50">
                <CardContent className="p-0">
                  <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-serif font-semibold mb-2">Send e-post</h3>
                  <a href="mailto:sydniemeny@gmail.com" className="text-secondary hover:underline break-all" data-testid="contact-email">sydniemeny@gmail.com</a>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="bg-white rounded-2xl p-8 shadow-soft border border-border/50 lg:col-span-2">
              <CardContent className="p-0">
                <h2 className="text-2xl font-serif font-semibold mb-2">Send oss en melding</h2>
                <p className="text-sm text-muted-foreground mb-6">Vi svarer så snart vi kan.</p>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-medium mb-1">Navn *</label>
                    <Input name="name" value={form.name} onChange={handleChange} required placeholder="Ditt navn" data-testid="contact-form-name" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">E-post</label>
                      <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="din@epost.no" data-testid="contact-form-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Telefon</label>
                      <Input name="phone" value={form.phone} onChange={handleChange} placeholder="+47 ..." data-testid="contact-form-phone" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Melding *</label>
                    <textarea name="message" value={form.message} onChange={handleChange} required rows={5} placeholder="Skriv meldingen din her..." className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-secondary resize-none" data-testid="contact-form-message" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-secondary hover:bg-secondary/90" data-testid="contact-form-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    {submitting ? 'Sender...' : 'Send melding'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

// Admin Login Page
const AdminLoginPage = () => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('adminToken', res.data.token);
      navigate('/admin/dashboard');
    } catch (e) {
      toast.error('Feil passord');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md">
        <CardHeader><CardTitle className="text-2xl font-serif text-center">Admin Panel</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input type="password" placeholder="Passord" value={password} onChange={e => setPassword(e.target.value)} required data-testid="admin-password" />
            <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90" disabled={loading} data-testid="admin-login-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Logg inn'}</Button>
          </form>
          <p className="text-xs text-center text-muted-foreground mt-4">Standard passord: admin123</p>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteCode, setDeleteCode] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showRevenue, setShowRevenue] = useState(false);
  const [revenueDate, setRevenueDate] = useState('');
  const [revenueResult, setRevenueResult] = useState(null);
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [todayMenuEnabled, setTodayMenuEnabled] = useState(true);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showTestSmsDialog, setShowTestSmsDialog] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [testSmsLoading, setTestSmsLoading] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [filteredDate, setFilteredDate] = useState(null);
  const adminToken = localStorage.getItem('adminToken');
  const prevOrderCount = useRef(null);
  const audioCtxRef = useRef(null);

  // Activate audio on first user click (browser requires user gesture)
  useEffect(() => {
    const activateAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
    };
    document.addEventListener('click', activateAudio);
    return () => document.removeEventListener('click', activateAudio);
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const playTone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'triangle';
        gain.gain.setValueAtTime(0.5, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + start + dur);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur);
      };
      playTone(660, 0, 0.2);
      playTone(880, 0.25, 0.2);
      playTone(1100, 0.5, 0.2);
      playTone(1320, 0.75, 0.35);
    } catch (e) { console.log('Audio error:', e); }
  }, []);

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchOrders();
    fetchDeliveryStatus();
    fetchSmsStatus();
    fetchTodayMenuStatus();
    fetchUnreadMessages();
    const interval = setInterval(() => { fetchOrders(true); fetchUnreadMessages(); }, 15000);
    return () => clearInterval(interval);
  }, [adminToken, filter, navigate]);

  const fetchDeliveryStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/delivery`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setDeliveryEnabled(res.data.enabled);
    } catch (e) {}
  };

  const fetchSmsStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/sms-settings`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSmsEnabled(res.data.enabled);
    } catch (e) {}
  };

  const fetchTodayMenuStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/today-menu`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setTodayMenuEnabled(res.data.enabled);
    } catch (e) {}
  };

  const fetchUnreadMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setUnreadMessages(res.data.unread || 0);
    } catch (e) {}
  };

  const toggleDelivery = async () => {
    try {
      const res = await axios.patch(`${API}/admin/delivery/toggle`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setDeliveryEnabled(res.data.enabled);
      toast.success(res.data.message);
    } catch (e) { toast.error('Kunne ikke endre leveringsstatus'); }
  };

  const toggleSms = async () => {
    try {
      const res = await axios.patch(`${API}/admin/sms-settings/toggle`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSmsEnabled(res.data.enabled);
      toast.success(res.data.message);
    } catch (e) { toast.error('Kunne ikke endre SMS-status'); }
  };

  const sendTestSms = async () => {
    if (!testSmsPhone.trim()) { toast.error('Skriv inn et telefonnummer'); return; }
    setTestSmsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/test-sms`, { phone: testSmsPhone }, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (res.data.success) {
        toast.success(res.data.message);
        setShowTestSmsDialog(false);
        setTestSmsPhone('');
      } else {
        toast.error(`Feil: ${res.data.error}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende test-SMS');
    } finally {
      setTestSmsLoading(false);
    }
  };


  const toggleTodayMenu = async () => {
    try {
      const res = await axios.patch(`${API}/admin/today-menu/toggle`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setTodayMenuEnabled(res.data.enabled);
      toast.success(res.data.message);
    } catch (e) { toast.error('Kunne ikke endre I dag meny status'); }
  };

  const fetchOrders = async (silent) => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/admin/orders${params}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      const newTotal = res.data.stats.total || 0;
      if (prevOrderCount.current !== null && newTotal > prevOrderCount.current) {
        playNotificationSound();
        toast.success(`Ny bestilling mottatt!`);
      }
      prevOrderCount.current = newTotal;
      setOrders(res.data.orders);
      setStats(res.data.stats);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { localStorage.removeItem('adminToken'); navigate('/admin'); }
    } finally {
      setLoading(false);
    }
  };

  const deleteOrder = async () => {
    setDeleting(true);
    try {
      const idsToDelete = deleteOrderId ? [deleteOrderId] : selectedOrders;
      const res = await axios.post(`${API}/admin/orders/bulk-delete`, { code: deleteCode, order_ids: idsToDelete }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(res.data.message);
      setDeleteOrderId(null);
      setShowDeleteDialog(false);
      setDeleteCode('');
      setSelectedOrders([]);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke slette');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  const calcRevenueForDate = async (dateStr) => {
    setRevenueDate(dateStr);
    if (!dateStr) { setRevenueResult(null); return; }
    try {
      const res = await axios.get(`${API}/admin/revenue/${dateStr}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setRevenueResult(res.data);
    } catch (e) {
      setRevenueResult({ total: 0, count: 0, date: dateStr });
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      const res = await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (res.data.sms_sent) {
        toast.success(`Status oppdatert! SMS ${res.data.sms_message?.includes('mock') ? '(mock)' : ''} sendt til kunden`);
      } else {
        toast.success('Status oppdatert');
      }
      fetchOrders();
    } catch (e) { toast.error('Kunne ikke oppdatere status'); }
  };

  const statusColors = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-blue-100 text-blue-800', preparing: 'bg-purple-100 text-purple-800', ready: 'bg-green-100 text-green-800', delivered: 'bg-gray-100 text-gray-800', cancelled: 'bg-red-100 text-red-800' };
  const statusLabels = { pending: 'Venter', confirmed: 'Bekreftet', preparing: 'Tilberedes', ready: 'Klar', delivered: 'Levert', cancelled: 'Kansellert' };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      <header className="bg-primary text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-serif font-bold">Sydnie's Meny - Admin</h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="text-white bg-white/20">Bestillinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/menu')}>Meny</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/customers')}>Kunder</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/messages')} data-testid="nav-admin-messages-dashboard">Meldinger{unreadMessages > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full" data-testid="unread-badge">{unreadMessages}</span>}</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/inventory')}>📦 Inventar</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/settings')}>Innstillinger</Button>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={toggleTodayMenu} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${todayMenuEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`} data-testid="today-menu-toggle">
            ⚡ I dag: {todayMenuEnabled ? 'PÅ' : 'AV'}
          </button>
          <button onClick={toggleSms} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${smsEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`} data-testid="sms-toggle">
            <MessageSquare className="w-4 h-4" /> SMS: {smsEnabled ? 'PÅ' : 'AV'}
          </button>
          <button onClick={() => setShowTestSmsDialog(true)} className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 transition-all" data-testid="test-sms-btn" title="Test SMS">
            🧪 Test SMS
          </button>
          <button onClick={toggleDelivery} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${deliveryEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`} data-testid="delivery-toggle">
            🚗 Levering: {deliveryEnabled ? 'PÅ' : 'AV'}
          </button>
          <Button variant="ghost" className="text-white hover:text-white/80" onClick={() => { localStorage.removeItem('adminToken'); navigate('/admin'); }}><LogOut className="w-4 h-4 mr-2" />Logg ut</Button>
        </div>
      </header>

      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center"><Package className="w-5 h-5 text-yellow-600" /></div><div><p className="text-2xl font-bold">{stats.pending || 0}</p><p className="text-xs text-muted-foreground">Ventende</p></div></div></Card>
          <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold">{stats.total || 0}</p><p className="text-xs text-muted-foreground">Totalt</p></div></div></Card>
          <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><DollarSign className="w-5 h-5 text-green-600" /></div><div><p className="text-2xl font-bold">kr {stats.today_revenue || 0}</p><p className="text-xs text-muted-foreground">Levert i dag</p></div></div></Card>
          <Card className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-purple-600" /></div><div><p className="text-2xl font-bold">{stats.delivered || 0}</p><p className="text-xs text-muted-foreground">Levert</p></div></div></Card>
        </div>

        {/* Revenue Lookup Button */}
        <div className="mb-6">
          <Button variant="outline" size="sm" className="bg-green-50 border-green-200 hover:bg-green-100 text-green-700" onClick={() => setShowRevenue(!showRevenue)} data-testid="revenue-lookup-btn">
            <DollarSign className="w-4 h-4 mr-2" />Sjekk dagsomsetning
          </Button>
          {showRevenue && (
            <Card className="mt-3 p-4 inline-flex items-center gap-4">
              <input
                type="date"
                value={revenueDate}
                onChange={e => calcRevenueForDate(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="revenue-date-picker"
              />
              {revenueResult && (
                <div className="flex items-center gap-3" data-testid="revenue-result">
                  <span className="text-2xl font-bold text-green-700">kr {revenueResult.total}</span>
                  <span className="text-sm text-muted-foreground">({revenueResult.count} levert)</span>
                </div>
              )}
              {revenueDate && revenueResult && revenueResult.count === 0 && (
                <span className="text-sm text-muted-foreground">Ingen leverte bestillinger denne dagen</span>
              )}
            </Card>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2 flex-wrap items-center">
            {orders.length > 0 && (
              <label className="flex items-center gap-2 mr-2 cursor-pointer select-none" data-testid="select-all-orders">
                <input type="checkbox" checked={selectedOrders.length === orders.length && orders.length > 0} onChange={toggleSelectAll} className="w-4 h-4 rounded border-gray-300 accent-secondary" />
                <span className="text-sm text-muted-foreground">Alle</span>
              </label>
            )}
            {['all', 'pending', 'confirmed', 'preparing', 'ready', 'delivered'].map(s => (
              <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm" className={filter === s ? 'bg-secondary' : ''} onClick={() => setFilter(s)} data-testid={`filter-${s}`}>
                {s === 'all' ? 'Alle' : statusLabels[s]} {s !== 'all' && stats[s] ? `(${stats[s]})` : ''}
              </Button>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            {selectedOrders.length > 0 && (
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setShowDeleteDialog(true)} data-testid="delete-selected-btn">
                <Trash2 className="w-4 h-4 mr-2" />Slett ({selectedOrders.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={fetchOrders} data-testid="refresh-orders"><RefreshCw className="w-4 h-4 mr-2" />Oppdater</Button>
          </div>
        </div>

        {/* Monthly total when viewing delivered orders */}
        {filter === 'delivered' && orders.length > 0 && (() => {
          // Group orders by month → days
          const monthData = {};
          orders.forEach(o => {
            const d = new Date(o.created_at);
            const monthKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
            const monthLabel = d.toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' });
            const dayKey = `${monthKey}-${String(d.getDate()).padStart(2,'0')}`;
            const dayLabel = d.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long' });
            if (!monthData[monthKey]) monthData[monthKey] = { label: monthLabel, total: 0, days: {} };
            monthData[monthKey].total += (o.total_amount || 0);
            if (!monthData[monthKey].days[dayKey]) monthData[monthKey].days[dayKey] = { label: dayLabel, total: 0, count: 0 };
            monthData[monthKey].days[dayKey].total += (o.total_amount || 0);
            monthData[monthKey].days[dayKey].count += 1;
          });
          const sortedMonths = Object.entries(monthData).sort((a,b) => b[0].localeCompare(a[0]));
          return (
            <div className="mb-6 space-y-2" data-testid="monthly-totals">
              {filteredDate && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between items-center mb-3">
                  <span className="text-blue-900 font-medium">Viser bestillinger for: <strong className="capitalize">{filteredDate.label}</strong></span>
                  <Button size="sm" variant="outline" onClick={() => setFilteredDate(null)} data-testid="clear-date-filter">✕ Fjern filter</Button>
                </div>
              )}
              {sortedMonths.map(([monthKey, m]) => {
                const isExpanded = expandedMonth === monthKey;
                return (
                  <div key={monthKey}>
                    <Card 
                      className="p-4 bg-green-50 border-green-200 flex justify-between items-center cursor-pointer hover:bg-green-100 transition-all"
                      onClick={() => setExpandedMonth(isExpanded ? null : monthKey)}
                      data-testid={`month-${monthKey}`}
                    >
                      <span className="font-semibold capitalize text-green-800 flex items-center gap-2">
                        <span className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                        {m.label}
                      </span>
                      <span className="text-2xl font-bold text-green-700">kr {m.total.toFixed(0)}</span>
                    </Card>
                    {isExpanded && (
                      <div className="ml-6 mt-2 space-y-1" data-testid={`days-${monthKey}`}>
                        {Object.entries(m.days).sort((a,b) => b[0].localeCompare(a[0])).map(([dayKey, d]) => (
                          <Card 
                            key={dayKey}
                            className={`p-3 flex justify-between items-center cursor-pointer transition-all ${
                              filteredDate?.key === dayKey ? 'bg-blue-100 border-blue-400' : 'bg-white hover:bg-blue-50'
                            }`}
                            onClick={() => setFilteredDate(filteredDate?.key === dayKey ? null : { key: dayKey, label: d.label })}
                            data-testid={`day-${dayKey}`}
                          >
                            <div>
                              <span className="font-medium capitalize text-gray-800">{d.label}</span>
                              <span className="text-xs text-muted-foreground ml-2">({d.count} bestillinger)</span>
                            </div>
                            <span className="text-lg font-bold text-green-700">kr {d.total.toFixed(0)}</span>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Orders */}
        <div className="space-y-4">
          {orders.length === 0 ? (
            <Card className="p-8 text-center"><p className="text-muted-foreground">Ingen bestillinger funnet</p></Card>
          ) : (() => {
            let lastDate = null;
            // Filter by selected date if any
            const visibleOrders = filteredDate
              ? orders.filter(o => {
                  const d = new Date(o.created_at);
                  const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                  return key === filteredDate.key;
                })
              : orders;
            // Pre-calculate daily delivered totals
            const dailyTotals = {};
            visibleOrders.forEach(o => {
              const d = new Date(o.created_at).toLocaleDateString('nb-NO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
              if (o.status === 'delivered') {
                dailyTotals[d] = (dailyTotals[d] || 0) + (o.total_amount || 0);
              }
            });
            if (visibleOrders.length === 0) {
              return <Card className="p-8 text-center"><p className="text-muted-foreground">Ingen bestillinger på valgt dag</p></Card>;
            }
            return visibleOrders.map((order, idx) => {
              const orderDate = new Date(order.created_at).toLocaleDateString('nb-NO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
              const showDateHeader = orderDate !== lastDate;
              lastDate = orderDate;
              return (
                <div key={`${order.id}-${idx}`}>
                  {showDateHeader && (
                    <div className="bg-primary text-white py-3 px-6 rounded-xl mb-4 mt-6 first:mt-0 flex justify-between items-center">
                      <h2 className="text-xl font-bold capitalize">{orderDate}</h2>
                      {dailyTotals[orderDate] > 0 && (
                        <span className="bg-white/20 px-4 py-1 rounded-full text-sm font-semibold" data-testid={`daily-total-${orderDate}`}>
                          Levert: kr {dailyTotals[orderDate]}
                        </span>
                      )}
                    </div>
                  )}
                  <Card className={`p-6 transition-all ${selectedOrders.includes(order.id) ? 'ring-2 ring-secondary bg-secondary/5' : ''}`} data-testid={`admin-order-${order.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex gap-4 flex-1">
                        <label className="flex items-start pt-1 cursor-pointer">
                          <input type="checkbox" checked={selectedOrders.includes(order.id)} onChange={() => toggleSelectOrder(order.id)} className="w-4 h-4 rounded border-gray-300 accent-secondary mt-0.5" data-testid={`select-order-${order.id}`} />
                        </label>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={statusColors[order.status]}>{statusLabels[order.status]}</Badge>
                            <span className="font-mono text-xs text-muted-foreground">{order.id}</span>
                          {/* Payment Method Badge */}
                          <Badge className={
                            order.payment_method === 'vipps' ? 'bg-orange-100 text-orange-800' :
                            order.payment_method === 'wise' ? 'bg-green-100 text-green-800' :
                            order.payment_method === 'kontant' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {order.payment_method === 'vipps' ? '📱 Vipps' :
                             order.payment_method === 'wise' ? '🌐 Wise' :
                             order.payment_method === 'kontant' ? '💵 Kontant' :
                             '💳 Ukjent'}
                          </Badge>
                        </div>
                        <h3 className="font-semibold">{order.customer_name}</h3>
                        <p className="text-sm text-muted-foreground">{order.customer_phone} · {order.pickup ? 'Henting' : order.delivery_address}</p>
                        {order.scheduled_time && (
                          <p className="text-sm font-medium text-accent mt-1">
                            ⏰ Ønsket tid: {new Date(order.scheduled_time).toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short' })} kl. {new Date(order.scheduled_time).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                        {!order.scheduled_time && <p className="text-sm font-medium text-green-600 mt-1">⚡ Så fort som mulig</p>}
                        <div className="mt-2 text-sm">{order.items.map((item, i) => <span key={i} className="mr-3">{item.name} x{item.quantity}</span>)}</div>
                        <p className="mt-2 font-semibold text-secondary">kr {order.total_amount}</p>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(order.created_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
                          <SelectTrigger className="w-[140px]" data-testid={`status-select-${order.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Venter</SelectItem>
                            <SelectItem value="confirmed">Bekreftet</SelectItem>
                            <SelectItem value="preparing">Tilberedes</SelectItem>
                            <SelectItem value="ready">Klar</SelectItem>
                            <SelectItem value="delivered">Levert</SelectItem>
                            <SelectItem value="cancelled">Kansellert</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Delete Orders Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowDeleteDialog(false); setDeleteCode(''); }}>
          <Card className="p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Slett {selectedOrders.length} bestilling{selectedOrders.length > 1 ? 'er' : ''}</h3>
                <p className="text-sm text-muted-foreground">Dette kan ikke angres!</p>
              </div>
            </div>
            <p className="text-sm mb-4">Skriv inn admin-koden for å bekrefte sletting.</p>
            <input
              type="password"
              placeholder="Skriv kode..."
              value={deleteCode}
              onChange={e => setDeleteCode(e.target.value)}
              className="w-full border rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="delete-code-input"
              onKeyDown={e => { if (e.key === 'Enter' && deleteCode) deleteOrder(); }}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeleteCode(''); }} data-testid="delete-cancel">Avbryt</Button>
              <Button variant="destructive" disabled={!deleteCode || deleting} onClick={deleteOrder} data-testid="delete-confirm">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Slett ({selectedOrders.length})
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Test SMS Dialog */}
      <Dialog open={showTestSmsDialog} onOpenChange={setShowTestSmsDialog}>
        <DialogContent className="sm:max-w-md" data-testid="test-sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🧪 Test SMS</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Send en test-SMS til ditt eget norske telefonnummer for å sjekke at SMS-tjenesten fungerer.
            </p>
            <div>
              <label className="block text-sm font-medium mb-2">Norsk telefonnummer (8 siffer)</label>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-2 rounded-xl bg-gray-100 border border-gray-200 text-sm font-medium">🇳🇴 +47</span>
                <Input
                  type="tel"
                  inputMode="numeric"
                  maxLength={8}
                  placeholder="40123456"
                  value={testSmsPhone}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                    setTestSmsPhone(digits);
                  }}
                  className="rounded-xl flex-1"
                  data-testid="test-sms-phone"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowTestSmsDialog(false); setTestSmsPhone(''); }}>Avbryt</Button>
              <Button onClick={sendTestSms} disabled={testSmsLoading || !testSmsPhone} className="bg-blue-500 hover:bg-blue-600" data-testid="test-sms-send">
                {testSmsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                Send test-SMS
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
const AdminMenuPage = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('adminToken');
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', price: '', category: '', image_url: '', available: true, menu_type: 'today' });
  const [uploading, setUploading] = useState(false);
  const [activeMenuType, setActiveMenuType] = useState('today');
  const [menuEnabled, setMenuEnabled] = useState({ today: true, later: true, soon: true, special: false });
  const [deliveryEnabled, setDeliveryEnabled] = useState({ later: true, soon: true });
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyItemId, setCopyItemId] = useState(null);
  const [copying, setCopying] = useState(false);
  const [specialMenu, setSpecialMenu] = useState(null);

  const menuTypeLabels = {
    today: { label: 'I dag', description: 'Så fort som mulig', color: 'bg-green-500', textColor: 'text-green-600' },
    later: { label: 'Senere', description: 'Innen 3 dager', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
    soon: { label: 'Snart', description: 'Store bestillinger', color: 'bg-orange-500', textColor: 'text-orange-600' },
    special: { label: specialMenu?.label || 'SKJERVØY DAGA', description: `Spesialmeny · ${specialMenu?.start_time || '08:00'}–${specialMenu?.end_time || '22:00'}`, color: 'bg-purple-500', textColor: 'text-purple-600' }
  };

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchMenu();
    fetchMenuStatuses();
    fetchDeliveryStatuses();
    axios.get(`${API}/settings/special-menu`).then(res => setSpecialMenu(res.data.special_menu || null)).catch(() => {});
  }, [adminToken, navigate, activeMenuType]);

  const fetchMenuStatuses = async () => {
    try {
      const [todayRes, laterRes, soonRes, specialRes] = await Promise.all([
        axios.get(`${API}/admin/today-menu`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/later-menu`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/soon-menu`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/settings/special-menu`).catch(() => ({ data: { special_menu: { enabled: false } } }))
      ]);
      setMenuEnabled({
        today: todayRes.data.enabled,
        later: laterRes.data.enabled,
        soon: soonRes.data.enabled,
        special: specialRes.data.special_menu?.enabled || false
      });
    } catch (e) {}
  };

  const fetchDeliveryStatuses = async () => {
    try {
      const [laterRes, soonRes] = await Promise.all([
        axios.get(`${API}/admin/later-delivery`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/soon-delivery`, { headers: { Authorization: `Bearer ${adminToken}` } })
      ]);
      setDeliveryEnabled({
        later: laterRes.data.enabled,
        soon: soonRes.data.enabled
      });
    } catch (e) {}
  };

  const toggleMenuType = async (type) => {
    try {
      if (type === 'special') {
        // Special menu uses a different endpoint (PATCH /admin/special-menu)
        const newEnabled = !(specialMenu?.enabled);
        const res = await axios.patch(
          `${API}/admin/special-menu`,
          { special_menu: { ...specialMenu, enabled: newEnabled } },
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        setSpecialMenu(res.data.special_menu);
        setMenuEnabled(prev => ({ ...prev, special: res.data.special_menu.enabled }));
        toast.success(`Spesialmeny ${res.data.special_menu.enabled ? 'aktivert' : 'deaktivert'}`);
        return;
      }
      const endpoint = type === 'today' ? 'today-menu' : type === 'later' ? 'later-menu' : 'soon-menu';
      const res = await axios.patch(`${API}/admin/${endpoint}/toggle`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setMenuEnabled(prev => ({ ...prev, [type]: res.data.enabled }));
      toast.success(res.data.message);
    } catch (e) { toast.error('Kunne ikke endre menystatus'); }
  };

  const toggleDelivery = async (type) => {
    try {
      const endpoint = type === 'later' ? 'later-delivery' : 'soon-delivery';
      const res = await axios.patch(`${API}/admin/${endpoint}/toggle`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setDeliveryEnabled(prev => ({ ...prev, [type]: res.data.enabled }));
      toast.success(res.data.message);
    } catch (e) { toast.error('Kunne ikke endre leveringsstatus'); }
  };

  const fetchMenu = async () => {
    try {
      const res = await axios.get(`${API}/admin/menu?menu_type=${activeMenuType}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setMenuItems(res.data.items);
      setCategories(res.data.categories);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) { localStorage.removeItem('adminToken'); navigate('/admin'); }
    } finally {
      setLoading(false);
    }
  };

  const copyMenuItem = async (toType) => {
    setCopying(true);
    try {
      const res = await axios.post(`${API}/admin/menu/copy`, { item_id: copyItemId, to_type: toType }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(res.data.message);
      setShowCopyDialog(false);
      setCopyItemId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke kopiere');
    } finally {
      setCopying(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Kun JPG, PNG, GIF eller WebP er tillatt');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bildet er for stort. Maks 5MB');
      return;
    }
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/admin/upload`, uploadData, {
        headers: { 
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData({...formData, image_url: res.data.image_url});
      toast.success('Bilde lastet opp!');
    } catch (e) {
      toast.error('Kunne ikke laste opp bilde');
    } finally {
      setUploading(false);
    }
  };

  const openCreateDialog = () => {
    setEditItem(null);
    setFormData({ name: '', description: '', price: '', category: '', image_url: '', available: true, menu_type: activeMenuType, sale_discount_percent: '', sale_price_custom: '', sale_start: '', sale_end: '', stock: null, hide_when_empty: true });
    setShowDialog(true);
  };

  const openEditDialog = (item) => {
    setEditItem(item);
    // Pre-fill: if item has a sale_discount_percent that matches a standard option (5/10/20/30/40/50), use dropdown; else use custom price
    const standardPercents = [5, 10, 20, 30, 40, 50];
    const hasSale = item.sale_price && item.sale_price < item.price;
    const useDropdown = hasSale && standardPercents.includes(item.sale_discount_percent);
    setFormData({ 
      name: item.name, 
      description: item.description, 
      price: item.price.toString(), 
      category: item.category, 
      image_url: item.image_url || '', 
      available: item.available, 
      menu_type: item.menu_type || 'today', 
      sale_discount_percent: useDropdown ? item.sale_discount_percent.toString() : '', 
      sale_price_custom: (hasSale && !useDropdown) ? item.sale_price.toString() : '',
      sale_start: item.sale_start || '', 
      sale_end: item.sale_end || '',
      stock: item.stock === undefined ? null : item.stock,
      hide_when_empty: item.hide_when_empty !== false
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const priceNum = parseFloat(formData.price);
    const data = { ...formData, price: priceNum };
    // Handle sale fields - support both percent dropdown AND custom price
    if (formData.sale_discount_percent && formData.sale_discount_percent !== '') {
      const percent = parseInt(formData.sale_discount_percent);
      data.sale_discount_percent = percent;
      data.sale_price = Math.round(priceNum * (1 - percent / 100));
      data.sale_start = formData.sale_start || null;
      data.sale_end = formData.sale_end || null;
    } else if (formData.sale_price_custom && formData.sale_price_custom !== '') {
      const salePrice = parseFloat(formData.sale_price_custom);
      if (salePrice >= priceNum) {
        toast.error(`Tilbudspris (kr ${salePrice}) må være lavere enn original pris (kr ${priceNum})`);
        return;
      }
      data.sale_price = salePrice;
      data.sale_discount_percent = Math.round((1 - salePrice / priceNum) * 100);
      data.sale_start = formData.sale_start || null;
      data.sale_end = formData.sale_end || null;
    } else {
      // Clear sale if nothing selected
      delete data.sale_price;
      delete data.sale_discount_percent;
      delete data.sale_start;
      delete data.sale_end;
      if (editItem) data.clear_sale = true;
    }
    delete data.sale_price_custom;
    
    // Handle stock - null means unlimited (clear it)
    if (data.stock === null || data.stock === undefined || data.stock === '') {
      delete data.stock;
      if (editItem) data.clear_stock = true;
    }
    try {
      if (editItem) {
        await axios.put(`${API}/admin/menu/${editItem.id}`, data, { headers: { Authorization: `Bearer ${adminToken}` } });
        toast.success('Rett oppdatert!');
      } else {
        await axios.post(`${API}/admin/menu`, data, { headers: { Authorization: `Bearer ${adminToken}` } });
        toast.success('Rett opprettet!');
      }
      setShowDialog(false);
      fetchMenu();
    } catch (e) { toast.error('Kunne ikke lagre'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne retten?')) return;
    try {
      await axios.delete(`${API}/admin/menu/${id}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Rett slettet');
      fetchMenu();
    } catch (e) { toast.error('Kunne ikke slette'); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.patch(`${API}/admin/menu/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(res.data.message);
      fetchMenu();
    } catch (e) { toast.error('Kunne ikke oppdatere'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-menu-page">
      <header className="bg-primary text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-serif font-bold">Sydnie's Meny - Admin</h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/dashboard')}>Bestillinger</Button>
            <Button variant="ghost" size="sm" className="text-white bg-white/20">Meny</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/customers')}>Kunder</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/messages')}>Meldinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/inventory')}>📦 Inventar</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/settings')}>Innstillinger</Button>
          </div>
        </div>
        <Button variant="ghost" className="text-white hover:text-white/80" onClick={() => { localStorage.removeItem('adminToken'); navigate('/admin'); }}><LogOut className="w-4 h-4 mr-2" />Logg ut</Button>
      </header>

      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-serif font-bold">Administrer Meny</h2>
          <Button onClick={openCreateDialog} className="bg-secondary hover:bg-secondary/90" data-testid="add-menu-item-btn"><Plus className="w-4 h-4 mr-2" />Legg til rett</Button>
        </div>

        {/* Menu Type Tabs */}
        <div className="flex gap-2 mb-6">
          {Object.entries(menuTypeLabels).map(([type, info]) => (
            <div key={type} className="flex flex-col items-center gap-2">
              <button
                onClick={() => { setActiveMenuType(type); setLoading(true); }}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeMenuType === type 
                    ? `${info.color} text-white shadow-lg` 
                    : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`menu-type-${type}`}
              >
                <span className="block">{info.label}</span>
                <span className={`text-xs ${activeMenuType === type ? 'text-white/80' : 'text-gray-500'}`}>{info.description}</span>
              </button>
              <div className="flex gap-1">
                <button
                  onClick={() => toggleMenuType(type)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    menuEnabled[type] ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}
                  data-testid={`toggle-menu-${type}`}
                  title="Meny PÅ/AV"
                >
                  {menuEnabled[type] ? 'PÅ' : 'AV'}
                </button>
                {(type === 'later' || type === 'soon') && (
                  <button
                    onClick={() => toggleDelivery(type)}
                    className={`px-2 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                      deliveryEnabled[type] ? 'bg-blue-500 text-white' : 'bg-gray-400 text-white'
                    }`}
                    data-testid={`toggle-delivery-${type}`}
                    title="Levering PÅ/AV"
                  >
                    🚗 {deliveryEnabled[type] ? 'Lev' : 'Lev'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Menu Items by Category */}
        {(() => {
          const sortedCategories = [...categories].sort((a, b) => a.localeCompare(b, 'nb', { numeric: true }));
          return sortedCategories.map(category => (
            <div key={category} className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-primary">{category}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {menuItems.filter(item => item.category === category).map(item => {
                  const activeSale = getActiveSale(item);
                  return (
                  <Card key={item.id} className={`p-4 ${!item.available ? 'opacity-50' : ''} ${activeSale ? 'border-red-300 border-2' : ''}`} data-testid={`admin-menu-item-${item.id}`}>
                    <div className="flex gap-4">
                      {item.image_url && <img src={getImageUrl(item.image_url)} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />}
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="font-semibold">{item.name}</h4>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {activeSale && <Badge className="bg-red-600 text-white text-xs">🔥 TILBUD</Badge>}
                            {item.stock !== null && item.stock !== undefined && (
                              <Badge className={item.stock === 0 ? 'bg-red-100 text-red-800' : item.stock <= 5 ? 'bg-yellow-100 text-yellow-800' : 'bg-purple-100 text-purple-800'}>
                                📦 {item.stock}
                              </Badge>
                            )}
                            <Badge className={item.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{item.available ? 'Aktiv' : 'Inaktiv'}</Badge>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                        {activeSale ? (
                          <p className="mt-1">
                            <span className="text-red-600 font-bold">kr {activeSale}</span>
                            <span className="text-muted-foreground line-through ml-2 text-sm">kr {item.price}</span>
                          </p>
                        ) : (
                          <p className="text-secondary font-bold mt-1">kr {item.price}{item.sale_price && <span className="text-xs text-muted-foreground ml-2">(tilbud: kr {item.sale_price} - inaktiv)</span>}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(item)} data-testid={`edit-${item.id}`}>Rediger</Button>
                      <Button variant="outline" size="sm" onClick={() => { setCopyItemId(item.id); setShowCopyDialog(true); }} data-testid={`copy-${item.id}`}><ClipboardList className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => handleToggle(item.id)} data-testid={`toggle-${item.id}`}>{item.available ? 'Deaktiver' : 'Aktiver'}</Button>
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-${item.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </Card>
                  );
                })}
              </div>
            </div>
          ));
        })()}

        {menuItems.length === 0 && (
          <Card className="p-8 text-center">
            <UtensilsCrossed className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-2">Ingen retter i "{menuTypeLabels[activeMenuType].label}" menyen.</p>
            <p className="text-sm text-muted-foreground">Legg til din første rett for denne menyen!</p>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="menu-item-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editItem ? 'Rediger rett' : 'Legg til ny rett'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-sm font-medium mb-1">Navn *</label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="F.eks. Margherita Pizza" required data-testid="menu-item-name" /></div>
            <div><label className="block text-sm font-medium mb-1">Beskrivelse *</label><Textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Kort beskrivelse av retten" required data-testid="menu-item-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Pris (kr) *</label><Input type="number" step="0.01" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} placeholder="149" required data-testid="menu-item-price" /></div>
              <div><label className="block text-sm font-medium mb-1">Kategori *</label><Input value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} placeholder="F.eks. Pizza" required list="categories" data-testid="menu-item-category" /><datalist id="categories">{categories.map(c => <option key={c} value={c} />)}</datalist></div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Menytype *</label>
              <Select value={formData.menu_type} onValueChange={v => setFormData({...formData, menu_type: v})}>
                <SelectTrigger data-testid="menu-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">🟢 I dag - Så fort som mulig</SelectItem>
                  <SelectItem value="later">🟡 Senere - Innen 3 dager</SelectItem>
                  <SelectItem value="soon">🟠 Snart - Store bestillinger</SelectItem>
                  <SelectItem value="special">🎪 {specialMenu?.label || 'SKJERVØY DAGA'} - Spesialmeny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Bilde</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input 
                    type="file" 
                    accept="image/jpeg,image/png,image/gif,image/webp" 
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="flex-1"
                    data-testid="menu-item-image-upload"
                  />
                  {uploading && <Loader2 className="w-5 h-5 animate-spin" />}
                </div>
                <div className="text-xs text-muted-foreground">Eller lim inn URL:</div>
                <Input value={formData.image_url} onChange={e => setFormData({...formData, image_url: e.target.value})} placeholder="https://..." data-testid="menu-item-image" />
                {formData.image_url && <img src={getImageUrl(formData.image_url)} alt="Forhåndsvisning" className="w-20 h-20 object-cover rounded-lg mt-2" />}
              </div>
            </div>
            <div className="flex items-center gap-2"><input type="checkbox" id="available" checked={formData.available} onChange={e => setFormData({...formData, available: e.target.checked})} className="rounded" /><label htmlFor="available" className="text-sm">Tilgjengelig for bestilling</label></div>
            
            {/* Inventory / Lager */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-3" data-testid="stock-section">
              <div className="flex items-center gap-2">
                <span className="text-lg">📦</span>
                <h4 className="font-semibold text-purple-700">Lager (valgfritt)</h4>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-purple-700">Antall på lager</label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.stock === null || formData.stock === undefined ? '' : formData.stock}
                    onChange={e => {
                      const v = e.target.value;
                      setFormData({...formData, stock: v === '' ? null : parseInt(v)});
                    }}
                    placeholder="Tom = ubegrenset"
                    data-testid="menu-item-stock"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.hide_when_empty !== false}
                      onChange={e => setFormData({...formData, hide_when_empty: e.target.checked})}
                      className="rounded"
                      data-testid="menu-item-hide-when-empty"
                    />
                    Skjul automatisk når lager = 0
                  </label>
                </div>
              </div>
              <p className="text-xs text-purple-600">💡 La feltet stå tomt for ubegrenset lager. Lager trekkes ned automatisk ved hver bestilling.</p>
            </div>
            
            {/* Sale / Tilbud fields */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3" data-testid="sale-section">
              <div className="flex items-center gap-2">
                <span className="text-lg">🔥</span>
                <h4 className="font-semibold text-red-700">Tilbud (valgfritt)</h4>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-red-700">Rabatt</label>
                <Select 
                  value={formData.sale_discount_percent || 'none'} 
                  onValueChange={v => {
                    if (v === 'none') {
                      setFormData({...formData, sale_discount_percent: '', sale_price_custom: ''});
                    } else {
                      setFormData({...formData, sale_discount_percent: v, sale_price_custom: ''});
                    }
                  }}
                >
                  <SelectTrigger data-testid="menu-item-sale-percent">
                    <SelectValue placeholder="Velg rabatt..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ingen tilbud</SelectItem>
                    <SelectItem value="5">5% rabatt</SelectItem>
                    <SelectItem value="10">10% rabatt</SelectItem>
                    <SelectItem value="20">20% rabatt</SelectItem>
                    <SelectItem value="30">30% rabatt</SelectItem>
                    <SelectItem value="40">40% rabatt</SelectItem>
                    <SelectItem value="50">50% rabatt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Or custom price */}
              <div>
                <label className="block text-xs font-medium mb-1 text-red-700">Eller skriv inn egen tilbudspris (kr)</label>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  value={formData.sale_price_custom || ''}
                  onChange={e => {
                    const val = e.target.value;
                    const priceNum = parseFloat(formData.price);
                    const saleNum = parseFloat(val);
                    if (val === '') {
                      setFormData({...formData, sale_price_custom: ''});
                      return;
                    }
                    if (!isNaN(priceNum) && !isNaN(saleNum) && saleNum >= priceNum) {
                      toast.error(`Tilbudspris må være LAVERE enn original pris (kr ${priceNum})`);
                      return;
                    }
                    setFormData({...formData, sale_price_custom: val, sale_discount_percent: ''});
                  }}
                  placeholder="f.eks. 199"
                  data-testid="menu-item-sale-custom"
                />
              </div>
              
              {(formData.sale_discount_percent || formData.sale_price_custom) && formData.price && (() => {
                const priceNum = parseFloat(formData.price);
                let salePrice, percent;
                if (formData.sale_discount_percent) {
                  percent = parseInt(formData.sale_discount_percent);
                  salePrice = Math.round(priceNum * (1 - percent / 100));
                } else {
                  salePrice = parseFloat(formData.sale_price_custom);
                  percent = Math.round((1 - salePrice / priceNum) * 100);
                }
                if (isNaN(salePrice) || isNaN(percent)) return null;
                return (
                  <div className="bg-white border border-red-300 rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">Beregnet tilbudspris:</p>
                    <p className="text-2xl font-bold text-red-600">
                      kr {salePrice}
                      <span className="text-base text-muted-foreground line-through ml-2">kr {priceNum}</span>
                      <span className="text-sm text-red-600 ml-2">(-{percent}%)</span>
                    </p>
                  </div>
                );
              })()}
              
              {(formData.sale_discount_percent || formData.sale_price_custom) && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-red-700">Fra dato</label>
                    <Input 
                      type="date" 
                      value={formData.sale_start} 
                      onChange={e => setFormData({...formData, sale_start: e.target.value})} 
                      data-testid="menu-item-sale-start" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-red-700">Til dato</label>
                    <Input 
                      type="date" 
                      value={formData.sale_end} 
                      onChange={e => setFormData({...formData, sale_end: e.target.value})} 
                      data-testid="menu-item-sale-end" 
                    />
                  </div>
                </div>
              )}
              {(formData.sale_discount_percent || formData.sale_price_custom) && (
                <p className="text-xs text-red-600">💡 La datoene stå tomme for permanent tilbud</p>
              )}
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Avbryt</Button>
              <Button type="submit" className="flex-1 bg-secondary hover:bg-secondary/90" data-testid="save-menu-item-btn">{editItem ? 'Oppdater' : 'Opprett'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Copy Item Dialog */}
      {showCopyDialog && copyItemId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowCopyDialog(false); setCopyItemId(null); }}>
          <Card className="p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">Kopier til meny</h3>
            <p className="text-sm text-muted-foreground mb-4">Velg hvilken meny du vil kopiere varen til.</p>
            <div className="space-y-2">
              {Object.entries(menuTypeLabels).filter(([t]) => t !== activeMenuType).map(([type, info]) => (
                <button
                  key={type}
                  disabled={copying}
                  onClick={() => copyMenuItem(type)}
                  className={`w-full p-3 rounded-lg border-2 border-gray-200 hover:border-gray-400 text-left transition-all flex items-center justify-between`}
                  data-testid={`copy-to-${type}`}
                >
                  <span className="font-semibold">{info.label}</span>
                  {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4 text-muted-foreground" />}
                </button>
              ))}
            </div>
            <Button variant="outline" className="w-full mt-3" onClick={() => { setShowCopyDialog(false); setCopyItemId(null); }}>Avbryt</Button>
          </Card>
        </div>
      )}
    </div>
  );
};

// Admin Customers Page
const AdminCustomersPage = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('adminToken');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchCustomers();
  }, [adminToken, navigate]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setCustomers(res.data.customers);
    } catch (e) { toast.error('Kunne ikke hente kunder'); }
    finally { setLoading(false); }
  };

  const viewCustomer = async (userId) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/admin/customers/${userId}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSelectedCustomer(res.data.customer);
      setCustomerOrders(res.data.orders);
    } catch (e) { toast.error('Kunne ikke hente kundedetaljer'); }
    finally { setLoadingDetail(false); }
  };

  const toggleBlock = async (userId, block) => {
    try {
      await axios.patch(`${API}/admin/customers/${userId}/block`, { is_blocked: block }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(block ? 'Kunde blokkert' : 'Blokkering opphevet');
      fetchCustomers();
      if (selectedCustomer?.id === userId) {
        setSelectedCustomer(prev => ({ ...prev, is_blocked: block }));
      }
    } catch (e) { toast.error('Feil ved oppdatering'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-customers-page">
      <header className="bg-primary text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-serif font-bold">Sydnie's Meny - Admin</h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/dashboard')}>Bestillinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/menu')}>Meny</Button>
            <Button variant="ghost" size="sm" className="text-white bg-white/20">Kunder</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/messages')}>Meldinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/inventory')}>📦 Inventar</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/settings')}>Innstillinger</Button>
          </div>
        </div>
        <Button variant="ghost" className="text-white hover:text-white/80" onClick={() => { localStorage.removeItem('adminToken'); navigate('/admin'); }}><LogOut className="w-4 h-4 mr-2" />Logg ut</Button>
      </header>

      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Kunder ({customers.length})</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer List */}
          <div className="space-y-3">
            {customers.length === 0 ? (
              <Card className="p-8 text-center"><p className="text-muted-foreground">Ingen registrerte kunder</p></Card>
            ) : customers.map(c => (
              <Card key={c.id} className={`p-4 transition-all hover:shadow-md ${selectedCustomer?.id === c.id ? 'ring-2 ring-secondary' : ''} ${c.is_blocked ? 'opacity-60 bg-red-50' : ''}`} data-testid={`customer-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 cursor-pointer min-w-0" onClick={() => viewCustomer(c.id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{c.name || '(uten navn)'}</p>
                      {c.is_guest && <Badge className="bg-blue-100 text-blue-700 text-xs">Gjest</Badge>}
                      {c.is_blocked && <Badge className="bg-red-500 text-white text-xs">Blokkert</Badge>}
                    </div>
                    {c.email && <p className="text-sm text-muted-foreground truncate">{c.email}</p>}
                    <p className="text-sm text-muted-foreground">{c.phone}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium">{c.order_count} bestillinger</p>
                    <p className="text-sm text-green-600 font-semibold">kr {c.total_spent || 0}</p>
                    <Button
                      size="sm"
                      variant={c.is_blocked ? "outline" : "destructive"}
                      className="mt-2 h-7 text-xs"
                      onClick={(e) => { e.stopPropagation(); toggleBlock(c.id, !c.is_blocked); }}
                      data-testid={`block-${c.id}`}
                    >
                      {c.is_blocked ? '🔓 Opphev' : '🚫 Blokker'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Customer Detail */}
          <div>
            {loadingDetail ? (
              <Card className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></Card>
            ) : selectedCustomer ? (
              <Card className="p-6" data-testid="customer-detail">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{selectedCustomer.name || '(uten navn)'}</h3>
                    {selectedCustomer.is_guest && <Badge className="bg-blue-100 text-blue-700 text-xs mb-1">Gjest (ikke registrert)</Badge>}
                    {selectedCustomer.email && <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>}
                    <p className="text-sm text-muted-foreground">{selectedCustomer.phone}</p>
                    {selectedCustomer.created_at && <p className="text-xs text-muted-foreground mt-1">Registrert: {new Date(selectedCustomer.created_at).toLocaleDateString('nb-NO')}</p>}
                  </div>
                  <Button
                    variant={selectedCustomer.is_blocked ? "outline" : "destructive"}
                    size="sm"
                    onClick={() => toggleBlock(selectedCustomer.id, !selectedCustomer.is_blocked)}
                    data-testid="toggle-block-btn"
                  >
                    {selectedCustomer.is_blocked ? 'Opphev blokkering' : 'Blokker kunde'}
                  </Button>
                </div>

                {selectedCustomer.is_blocked && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-700 font-medium">Denne kunden er blokkert og kan ikke legge inn nye bestillinger.</p>
                  </div>
                )}

                <h4 className="font-semibold mb-3">Bestillingshistorikk ({customerOrders.length})</h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {customerOrders.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Ingen bestillinger</p>
                  ) : customerOrders.map(o => (
                    <div key={o.id} className="border rounded-lg p-3 text-sm" data-testid={`customer-order-${o.id}`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-mono text-xs">{o.id}</span>
                        <Badge className={o.status === 'delivered' ? 'bg-green-500 text-white' : o.status === 'cancelled' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'}>
                          {o.status === 'delivered' ? 'Levert' : o.status === 'cancelled' ? 'Kansellert' : o.status === 'pending' ? 'Venter' : o.status}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span>{o.items?.map(i => `${i.quantity}x ${i.name}`).join(', ')}</span>
                        <span className="font-semibold">kr {o.total_amount}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  ))}
                </div>
              </Card>
            ) : (
              <Card className="p-8 text-center"><p className="text-muted-foreground">Velg en kunde for å se detaljer</p></Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Settings Page (Opening Hours)
const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('adminToken');
  const [hours, setHours] = useState({
    monday: '', tuesday: '', wednesday: '', thursday: '', friday: '', saturday: '', sunday: ''
  });
  const [settings, setSettings] = useState({
    vipps_number: '',
    delivery_fee: 35,
    phone: '',
    email: '',
    address: '',
    available_times: ['10:00', '13:00', '16:00'],
    days_ahead: 7,
    closed_dates: []
  });
  const [newTime, setNewTime] = useState('');
  const [newClosedDate, setNewClosedDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [orderSlots, setOrderSlots] = useState({ today: [], later: [], soon: [], special: [] });
  const [activeSlotTab, setActiveSlotTab] = useState('today');
  const [newSlotTime, setNewSlotTime] = useState('');
  const [specialMenu, setSpecialMenu] = useState({ enabled: false, label: 'SKJERVØY DAGA', start_time: '08:00', end_time: '22:00' });
  const [savingSpecial, setSavingSpecial] = useState(false);

  const dayLabels = {
    monday: 'Mandag', tuesday: 'Tirsdag', wednesday: 'Onsdag', thursday: 'Torsdag',
    friday: 'Fredag', saturday: 'Lørdag', sunday: 'Søndag'
  };

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchSettings();
  }, [adminToken, navigate]);

  const fetchSettings = async () => {
    try {
      const [hoursRes, contactRes, laterDaysRes, soonDaysRes, asapRes, maxBookingsRes, disabledSlotsRes, orderSlotsRes, specialMenuRes] = await Promise.all([
        axios.get(`${API}/admin/hours`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/contact`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/settings/later-days`).catch(() => ({ data: { later_days: 5 } })),
        axios.get(`${API}/settings/soon-days`).catch(() => ({ data: { soon_days: 30 } })),
        axios.get(`${API}/settings/asap-enabled`).catch(() => ({ data: { enabled: true } })),
        axios.get(`${API}/settings/max-bookings`).catch(() => ({ data: { max_bookings: 2 } })),
        axios.get(`${API}/settings/disabled-slots`).catch(() => ({ data: { disabled_slots: [] } })),
        axios.get(`${API}/settings/order-slots`).catch(() => ({ data: { order_slots: { today: [], later: [], soon: [], special: [] } } })),
        axios.get(`${API}/settings/special-menu`).catch(() => ({ data: { special_menu: null } }))
      ]);
      setHours(hoursRes.data.hours);
      setSettings({
        vipps_number: contactRes.data.vipps_number || '',
        delivery_fee: contactRes.data.delivery_fee || 35,
        phone: contactRes.data.phone || '',
        email: contactRes.data.email || '',
        address: contactRes.data.address || '',
        available_times: contactRes.data.available_times || ['10:00', '13:00', '16:00'],
        days_ahead: contactRes.data.days_ahead || 7,
        closed_dates: contactRes.data.closed_dates || [],
        later_days: laterDaysRes.data.later_days || 5,
        soon_days: soonDaysRes.data.soon_days || 30,
        asap_enabled: asapRes.data.enabled !== false,
        max_bookings: maxBookingsRes.data.max_bookings || 2,
        disabled_slots: disabledSlotsRes.data.disabled_slots || []
      });
      const slots = orderSlotsRes.data.order_slots || {};
      setOrderSlots({
        today: slots.today || [],
        later: slots.later || [],
        soon: slots.soon || [],
        special: slots.special || []
      });
      setSpecialMenu(specialMenuRes.data.special_menu || { enabled: false, label: 'SKJERVØY DAGA', start_time: '08:00', end_time: '22:00' });
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('adminToken'); navigate('/admin'); }
    } finally { setLoading(false); }
  };

  const toggleAsap = async () => {
    try {
      const res = await axios.patch(`${API}/admin/asap-enabled`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSettings({...settings, asap_enabled: res.data.enabled});
      toast.success(res.data.message);
    } catch (e) { toast.error('Kunne ikke endre status'); }
  };

  const updateMaxBookings = async (val) => {
    try {
      await axios.patch(`${API}/admin/max-bookings`, { max_bookings: val }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSettings({...settings, max_bookings: val});
      toast.success(`Maks bestillinger oppdatert til ${val} per tid`);
    } catch (e) { toast.error('Kunne ikke oppdatere'); }
  };

  const toggleSlot = async (slot) => {
    const current = settings.disabled_slots || [];
    const updated = current.includes(slot) ? current.filter(s => s !== slot) : [...current, slot];
    try {
      await axios.patch(`${API}/admin/disabled-slots`, { disabled_slots: updated }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSettings({...settings, disabled_slots: updated});
    } catch (e) { toast.error('Kunne ikke oppdatere'); }
  };

  const addNewSlot = () => {
    if (!newSlotTime) { toast.error('Velg en tid først'); return; }
    const list = orderSlots[activeSlotTab] || [];
    if (list.find(s => s.time === newSlotTime)) {
      toast.error('Tiden finnes allerede');
      return;
    }
    const updated = [...list, { time: newSlotTime, available: true, delivery_available: true }].sort((a,b) => a.time.localeCompare(b.time));
    setOrderSlots({ ...orderSlots, [activeSlotTab]: updated });
    setNewSlotTime('');
  };

  const removeSlot = (idx) => {
    const list = [...(orderSlots[activeSlotTab] || [])];
    list.splice(idx, 1);
    setOrderSlots({ ...orderSlots, [activeSlotTab]: list });
  };

  const toggleSlotField = (idx, field) => {
    const list = [...(orderSlots[activeSlotTab] || [])];
    list[idx] = { ...list[idx], [field]: !list[idx][field] };
    setOrderSlots({ ...orderSlots, [activeSlotTab]: list });
  };

  const saveOrderSlots = async () => {
    try {
      await axios.patch(`${API}/admin/order-slots`, { order_slots: orderSlots }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Bestillingstider lagret!');
    } catch (e) { toast.error('Kunne ikke lagre'); }
  };

  const autofillSlots = () => {
    const defaultSlots = [];
    // For SKJERVØY DAGA, use its own time range
    if (activeSlotTab === 'special' && specialMenu) {
      const parse = (t) => {
        const m = String(t || '').match(/^(\d{1,2}):(\d{2})$/);
        return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : null;
      };
      const start = parse(specialMenu.start_time) ?? 8 * 60;
      const end = parse(specialMenu.end_time) ?? 22 * 60;
      for (let t = start; t <= end; t += 30) {
        const h = Math.floor(t / 60);
        const m = t % 60;
        defaultSlots.push({ time: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`, available: true, delivery_available: true });
      }
    } else {
      // Default 14:00 - 19:00 every 30 min
      for (let h = 14; h <= 19; h++) {
        defaultSlots.push({ time: `${String(h).padStart(2,'0')}:00`, available: true, delivery_available: true });
        if (h < 19) defaultSlots.push({ time: `${String(h).padStart(2,'0')}:30`, available: true, delivery_available: true });
      }
    }
    setOrderSlots({ ...orderSlots, [activeSlotTab]: defaultSlots });
    toast.success(`Standard tider fylt inn for ${activeSlotTab === 'today' ? 'I DAG' : activeSlotTab === 'later' ? 'SENERE' : activeSlotTab === 'special' ? (specialMenu?.label || 'SKJERVØY DAGA') : 'SNART'}`);
  };

  const addTime = () => {
    if (newTime && !settings.available_times.includes(newTime)) {
      const updatedTimes = [...settings.available_times, newTime].sort();
      setSettings({...settings, available_times: updatedTimes});
      setNewTime('');
    }
  };

  const removeTime = (time) => {
    setSettings({...settings, available_times: settings.available_times.filter(t => t !== time)});
  };

  const addClosedDate = () => {
    if (newClosedDate && !settings.closed_dates.includes(newClosedDate)) {
      const updatedDates = [...settings.closed_dates, newClosedDate].sort();
      setSettings({...settings, closed_dates: updatedDates});
      setNewClosedDate('');
    }
  };

  const removeClosedDate = (date) => {
    setSettings({...settings, closed_dates: settings.closed_dates.filter(d => d !== date)});
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/hours`, hours, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Åpningstider oppdatert!');
    } catch (e) { toast.error('Kunne ikke lagre'); }
    finally { setSaving(false); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await Promise.all([
        axios.put(`${API}/admin/contact`, settings, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.patch(`${API}/admin/later-days`, { later_days: settings.later_days }, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.patch(`${API}/admin/soon-days`, { soon_days: settings.soon_days }, { headers: { Authorization: `Bearer ${adminToken}` } })
      ]);
      toast.success('Innstillinger oppdatert!');
    } catch (e) { toast.error('Kunne ikke lagre'); }
    finally { setSavingSettings(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-settings-page">
      <header className="bg-primary text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-serif font-bold">Sydnie's Meny - Admin</h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/dashboard')}>Bestillinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/menu')}>Meny</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/customers')}>Kunder</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/messages')}>Meldinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/inventory')}>📦 Inventar</Button>
            <Button variant="ghost" size="sm" className="text-white bg-white/20">Innstillinger</Button>
          </div>
        </div>
        <Button variant="ghost" className="text-white hover:text-white/80" onClick={() => { localStorage.removeItem('adminToken'); navigate('/admin'); }}><LogOut className="w-4 h-4 mr-2" />Logg ut</Button>
      </header>

      <div className="p-6 max-w-2xl mx-auto">
        <h2 className="text-2xl font-serif font-bold mb-6">Åpningstider</h2>
        <Card className="p-6">
          <div className="space-y-4">
            {Object.entries(dayLabels).map(([key, label]) => {
              const isClosed = hours[key]?.toLowerCase().trim() === 'stengt';
              return (
                <div key={key} className="flex items-center gap-3">
                  <label className="w-24 font-medium">{label}</label>
                  <Input
                    value={isClosed ? '' : (hours[key] || '')}
                    onChange={e => setHours({...hours, [key]: e.target.value})}
                    placeholder="f.eks. 14:00 - 19:00"
                    className="flex-1"
                    disabled={isClosed}
                    data-testid={`hours-${key}`}
                  />
                  <button
                    type="button"
                    onClick={() => setHours({...hours, [key]: isClosed ? '14:00 - 19:00' : 'Stengt'})}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      isClosed 
                        ? 'bg-red-500 text-white hover:bg-red-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`toggle-closed-${key}`}
                  >
                    {isClosed ? '🔴 Stengt' : 'Sett stengt'}
                  </button>
                </div>
              );
            })}
          </div>
          <Button onClick={handleSaveHours} disabled={saving} className="w-full mt-6 bg-secondary hover:bg-secondary/90" data-testid="save-hours-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Lagrer...</> : 'Lagre åpningstider'}
          </Button>
        </Card>
        <p className="text-sm text-muted-foreground mt-4 text-center">Tips: Skriv "Stengt" for dager restauranten er lukket</p>

        <h2 className="text-2xl font-serif font-bold mb-6 mt-10">Restaurant Innstillinger</h2>
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">Leveringsgebyr</label>
              <Input
                type="number"
                value={settings.delivery_fee}
                onChange={e => setSettings({...settings, delivery_fee: parseInt(e.target.value) || 0})}
                placeholder="35"
                className="flex-1"
                data-testid="delivery-fee"
              />
              <span className="text-muted-foreground">kr</span>
            </div>
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">Min. levering</label>
              <Input
                type="number"
                value={settings.min_delivery_amount || 200}
                onChange={e => setSettings({...settings, min_delivery_amount: parseInt(e.target.value) || 0})}
                placeholder="200"
                className="flex-1"
                data-testid="min-delivery-amount"
              />
              <span className="text-muted-foreground">kr</span>
            </div>
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">Senere-dager</label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings.later_days || 5}
                onChange={e => setSettings({...settings, later_days: parseInt(e.target.value) || 5})}
                placeholder="5"
                className="flex-1"
                data-testid="later-days"
              />
              <span className="text-muted-foreground">dager</span>
            </div>
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">Snart-dager</label>
              <Input
                type="number"
                min="7"
                max="90"
                value={settings.soon_days || 30}
                onChange={e => setSettings({...settings, soon_days: parseInt(e.target.value) || 30})}
                placeholder="30"
                className="flex-1"
                data-testid="soon-days"
              />
              <span className="text-muted-foreground">dager</span>
            </div>
            
            {/* ASAP toggle + max bookings per slot */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <label className="font-medium block">⚡ Så fort som mulig</label>
                  <p className="text-xs text-muted-foreground">La kunder bestille umiddelbart uten å velge tid</p>
                </div>
                <button
                  type="button"
                  onClick={toggleAsap}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    settings.asap_enabled 
                      ? 'bg-green-500 text-white hover:bg-green-600' 
                      : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                  }`}
                  data-testid="toggle-asap"
                >
                  {settings.asap_enabled ? '✅ PÅ' : '❌ AV'}
                </button>
              </div>
              <div className="flex items-center gap-4 pt-3 border-t border-blue-200">
                <div className="flex-1">
                  <label className="font-medium block">📊 Maks bestillinger per tid</label>
                  <p className="text-xs text-muted-foreground">F.eks. 3 = maks 3 kunder per tidspunkt (kl 14:00, kl 15:00 osv.)</p>
                </div>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={settings.max_bookings || 2}
                  onChange={e => {
                    const v = parseInt(e.target.value) || 1;
                    setSettings({...settings, max_bookings: v});
                  }}
                  onBlur={e => updateMaxBookings(parseInt(e.target.value) || 1)}
                  className="w-24"
                  data-testid="max-bookings"
                />
                <span className="text-muted-foreground">stk</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">Vipps-nummer</label>
              <Input
                value={settings.vipps_number}
                onChange={e => setSettings({...settings, vipps_number: e.target.value})}
                placeholder="46677879"
                className="flex-1"
                data-testid="vipps-number"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">Telefon</label>
              <Input
                value={settings.phone}
                onChange={e => setSettings({...settings, phone: e.target.value})}
                placeholder="+47 466 77 879"
                className="flex-1"
                data-testid="phone"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">E-post</label>
              <Input
                value={settings.email}
                onChange={e => setSettings({...settings, email: e.target.value})}
                placeholder="epost@eksempel.no"
                className="flex-1"
                data-testid="email"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="w-32 font-medium">Adresse</label>
              <Input
                value={settings.address}
                onChange={e => setSettings({...settings, address: e.target.value})}
                placeholder="Gateadresse, Postnummer Sted"
                className="flex-1"
                data-testid="address"
              />
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full mt-6 bg-secondary hover:bg-secondary/90" data-testid="save-settings-btn">
            {savingSettings ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Lagrer...</> : 'Lagre innstillinger'}
          </Button>
        </Card>

        {/* ============ SKJERVØY DAGA SPECIAL MENU ============ */}
        <h2 className="text-2xl font-serif font-bold mb-6 mt-10">🎪 Spesialmeny ({specialMenu?.label || 'SKJERVØY DAGA'})</h2>
        <Card className="p-6 border-purple-200 bg-purple-50/30">
          <p className="text-sm text-muted-foreground mb-4">Aktiver en spesialmeny for en bestemt dag (f.eks. Skjervøy Daga). Når denne er PÅ, skjules "I dag"-menyen automatisk for kundene.</p>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white rounded-xl border">
              <div>
                <p className="font-semibold">Aktiver spesialmeny</p>
                <p className="text-xs text-muted-foreground">PÅ = "I dag" skjules, kunder bestiller fra spesialmenyen</p>
              </div>
              <button
                type="button"
                onClick={() => setSpecialMenu({ ...specialMenu, enabled: !specialMenu?.enabled })}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                  specialMenu?.enabled ? 'bg-purple-600' : 'bg-gray-300'
                }`}
                data-testid="special-menu-toggle"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  specialMenu?.enabled ? 'translate-x-8' : 'translate-x-1'
                }`} />
              </button>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Navn på menyen</label>
              <Input
                value={specialMenu?.label || ''}
                onChange={e => setSpecialMenu({ ...specialMenu, label: e.target.value })}
                placeholder="SKJERVØY DAGA"
                maxLength={50}
                data-testid="special-menu-label"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Starttid</label>
                <Input
                  type="time"
                  value={specialMenu?.start_time || '08:00'}
                  onChange={e => setSpecialMenu({ ...specialMenu, start_time: e.target.value })}
                  data-testid="special-menu-start"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sluttid</label>
                <Input
                  type="time"
                  value={specialMenu?.end_time || '22:00'}
                  onChange={e => setSpecialMenu({ ...specialMenu, end_time: e.target.value })}
                  data-testid="special-menu-end"
                />
              </div>
            </div>
            
            <div className="border-t border-purple-200 pt-4">
              <p className="text-sm font-semibold text-purple-700 mb-2">📅 Dato-område (valgfritt)</p>
              <p className="text-xs text-muted-foreground mb-3">La datoer stå tomme = alltid aktiv når PÅ. Sett datoer = kun aktiv mellom disse datoene (f.eks. 26.06.2026 - 27.06.2026 for Skjervøy Daga).</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Fra dato</label>
                  <Input
                    type="date"
                    value={specialMenu?.start_date || ''}
                    onChange={e => setSpecialMenu({ ...specialMenu, start_date: e.target.value || null })}
                    data-testid="special-menu-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Til dato</label>
                  <Input
                    type="date"
                    value={specialMenu?.end_date || ''}
                    onChange={e => setSpecialMenu({ ...specialMenu, end_date: e.target.value || null })}
                    data-testid="special-menu-end-date"
                    min={specialMenu?.start_date || undefined}
                  />
                </div>
              </div>
              {specialMenu?.enabled && specialMenu?.start_date && !specialMenu?.active_now && (
                <p className="text-xs text-orange-600 mt-2">⚠️ Menyen er PÅ men i dag er <strong>utenfor</strong> dato-området, så den vises ikke til kundene ennå.</p>
              )}
              {specialMenu?.enabled && specialMenu?.active_now && specialMenu?.start_date && (
                <p className="text-xs text-green-600 mt-2">✅ Menyen er aktiv i dag (innenfor dato-området).</p>
              )}
            </div>
            
            <Button
              onClick={async () => {
                setSavingSpecial(true);
                try {
                  await axios.patch(`${API}/admin/special-menu`, { special_menu: specialMenu }, { headers: { Authorization: `Bearer ${adminToken}` } });
                  toast.success('Spesialmeny oppdatert');
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Kunne ikke lagre');
                } finally {
                  setSavingSpecial(false);
                }
              }}
              disabled={savingSpecial}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="save-special-menu"
            >
              {savingSpecial ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Lagrer...</> : 'Lagre spesialmeny'}
            </Button>
            
            <p className="text-xs text-muted-foreground">💡 Tips: Etter aktivering, gå til "Meny" og legg til retter med menytypen "{specialMenu?.label || 'SKJERVØY DAGA'}". Legg deretter til tider i "Bestillingstider" → fanen "{specialMenu?.label || 'SKJERVØY DAGA'}".</p>
          </div>
        </Card>

        <h2 className="text-2xl font-serif font-bold mb-6 mt-10">Bestillingstider</h2>
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-4">Velg hvilke tider kunder kan bestille for, og om levering er tilgjengelig på den tiden.</p>
          
          {/* Menu type tabs */}
          <div className="flex gap-2 mb-6 border-b">
            {[
              { key: 'today', label: 'I DAG', color: 'green' },
              { key: 'later', label: 'SENERE', color: 'yellow' },
              { key: 'soon', label: 'SNART', color: 'blue' },
              { key: 'special', label: specialMenu?.label || 'SKJERVØY DAGA', color: 'purple' }
            ].map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveSlotTab(t.key)}
                className={`px-4 py-2 font-semibold transition-all border-b-2 ${
                  activeSlotTab === t.key 
                    ? 'border-secondary text-secondary' 
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Time slot table */}
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 rounded-lg font-semibold text-sm">
              <div className="col-span-3">⏰ Tid</div>
              <div className="col-span-4 text-center">✅ Ledig (kan bestille)</div>
              <div className="col-span-4 text-center">🚗 Levering</div>
              <div className="col-span-1 text-center"></div>
            </div>
            {(orderSlots[activeSlotTab] || []).length === 0 && activeSlotTab !== 'today' ? (
              <p className="text-sm text-muted-foreground italic py-4 text-center">Ingen tider lagt til. Klikk "Legg til tid" nedenfor.</p>
            ) : (
              <>
                {/* ASAP row - only for I DAG */}
                {activeSlotTab === 'today' && (
                  <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg" data-testid="slot-row-asap">
                    <div className="col-span-3 font-semibold flex items-center gap-2">⚡ Så fort som mulig</div>
                    <div className="col-span-4 text-center">
                      <button
                        type="button"
                        onClick={toggleAsap}
                        className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                          settings.asap_enabled 
                            ? 'bg-green-500 text-white hover:bg-green-600' 
                            : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                        }`}
                        data-testid="toggle-asap-row"
                      >
                        {settings.asap_enabled ? '✅ PÅ' : '❌ AV'}
                      </button>
                    </div>
                    <div className="col-span-4 text-center text-xs text-muted-foreground italic">
                      (kun henting)
                    </div>
                    <div className="col-span-1"></div>
                  </div>
                )}
                {(orderSlots[activeSlotTab] || []).length === 0 && activeSlotTab === 'today' && (
                  <p className="text-sm text-muted-foreground italic py-2 text-center">Ingen tider lagt til ennå. Klikk "Legg til tid" eller "Auto-fyll standard tider".</p>
                )}
                {(orderSlots[activeSlotTab] || []).map((slot, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-white border rounded-lg" data-testid={`slot-row-${slot.time}`}>
                <div className="col-span-3 font-semibold">kl {slot.time}</div>
                <div className="col-span-4 text-center">
                  <button
                    type="button"
                    onClick={() => toggleSlotField(idx, 'available')}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                      slot.available 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                    data-testid={`toggle-available-${slot.time}`}
                  >
                    {slot.available ? '✅ PÅ' : '❌ AV'}
                  </button>
                </div>
                <div className="col-span-4 text-center">
                  <button
                    type="button"
                    onClick={() => toggleSlotField(idx, 'delivery_available')}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                      slot.delivery_available 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                    data-testid={`toggle-delivery-${slot.time}`}
                  >
                    {slot.delivery_available ? '✅ PÅ' : '❌ AV'}
                  </button>
                </div>
                <div className="col-span-1 text-center">
                  <button
                    type="button"
                    onClick={() => removeSlot(idx)}
                    className="text-red-500 hover:text-red-700 text-lg"
                    data-testid={`remove-slot-${slot.time}`}
                    title="Slett tid"
                  >🗑️</button>
                </div>
              </div>
            ))}
              </>
            )}
          </div>

          {/* Add new slot */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t items-center">
            <Input
              type="time"
              value={newSlotTime}
              onChange={e => setNewSlotTime(e.target.value)}
              className="w-40"
              data-testid="new-slot-input"
            />
            <Button type="button" variant="outline" onClick={addNewSlot} data-testid="add-slot-btn">
              + Legg til tid
            </Button>
            <Button type="button" variant="outline" onClick={autofillSlots} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="autofill-slots">
              ⚡ Auto-fyll standard tider
            </Button>
            <Button type="button" onClick={saveOrderSlots} className="ml-auto bg-secondary hover:bg-secondary/90" data-testid="save-order-slots">
              💾 Lagre
            </Button>
          </div>
        </Card>

        <h2 className="text-2xl font-serif font-bold mb-6 mt-10">Stengte datoer</h2>
        <Card className="p-6">
          <p className="text-sm text-muted-foreground mb-4">Legg til datoer når du ikke tar imot bestillinger (f.eks. ferie, fullbooket)</p>
          <div className="space-y-4">
            <div>
              <label className="block font-medium mb-2">Datoer som er stengt</label>
              {settings.closed_dates.length === 0 ? (
                <p className="text-sm text-muted-foreground italic mb-3">Ingen stengte datoer</p>
              ) : (
                <div className="flex flex-wrap gap-2 mb-3">
                  {settings.closed_dates.map(date => (
                    <Badge key={date} variant="destructive" className="px-3 py-1 text-sm flex items-center gap-2">
                      🚫 {formatDate(date)}
                      <button type="button" onClick={() => removeClosedDate(date)} className="hover:text-white/70">×</button>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newClosedDate}
                  onChange={e => setNewClosedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-48"
                  data-testid="new-closed-date"
                />
                <Button type="button" variant="outline" onClick={addClosedDate} data-testid="add-closed-date-btn">Legg til stengt dato</Button>
              </div>
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full mt-6 bg-secondary hover:bg-secondary/90" data-testid="save-closed-dates-btn">
            {savingSettings ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Lagrer...</> : 'Lagre stengte datoer'}
          </Button>
        </Card>
        <p className="text-sm text-muted-foreground mt-4 text-center">Kunder kan ikke velge disse datoene ved bestilling</p>
      </div>
    </div>
  );
};


// Admin Inventory Page (Ingredients-based)
const AdminInventoryPage = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('adminToken');
  const [ingredients, setIngredients] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newStock, setNewStock] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  const [editingRecipe, setEditingRecipe] = useState([]);

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchAll();
  }, [adminToken, navigate]);

  const fetchAll = async () => {
    try {
      const [ingRes, menuRes] = await Promise.all([
        axios.get(`${API}/admin/ingredients`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/menu`, { headers: { Authorization: `Bearer ${adminToken}` } })
      ]);
      setIngredients(ingRes.data.ingredients || []);
      setMenuItems(menuRes.data.items || []);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('adminToken'); navigate('/admin'); }
    } finally { setLoading(false); }
  };

  const addIngredient = async () => {
    if (!newName.trim()) { toast.error('Skriv inn navn'); return; }
    try {
      await axios.post(`${API}/admin/ingredients`, { name: newName.trim(), stock: parseInt(newStock) || 0 }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Ingrediens lagt til');
      setNewName(''); setNewStock('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunne ikke opprette'); }
  };

  const updateStock = async (ing, newVal) => {
    setSavingId(ing.id);
    try {
      await axios.put(`${API}/admin/ingredients/${ing.id}`, { stock: newVal }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setIngredients(prev => prev.map(i => i.id === ing.id ? { ...i, stock: newVal } : i));
    } catch (e) { toast.error('Kunne ikke oppdatere'); }
    finally { setSavingId(null); }
  };

  const toggleHide = async (ing) => {
    try {
      const newVal = !(ing.hide_when_empty !== false);
      await axios.put(`${API}/admin/ingredients/${ing.id}`, { hide_when_empty: newVal }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setIngredients(prev => prev.map(i => i.id === ing.id ? { ...i, hide_when_empty: newVal } : i));
    } catch (e) { toast.error('Kunne ikke oppdatere'); }
  };

  const deleteIngredient = async (ing) => {
    if (!window.confirm(`Slett ingrediensen "${ing.name}"? Den fjernes også fra alle oppskrifter.`)) return;
    try {
      await axios.delete(`${API}/admin/ingredients/${ing.id}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Slettet');
      fetchAll();
    } catch (e) { toast.error('Kunne ikke slette'); }
  };

  const openRecipeEditor = (item) => {
    setEditingRecipeId(item.id);
    setEditingRecipe(item.recipe || []);
  };

  const addRecipeRow = () => {
    setEditingRecipe([...editingRecipe, { ingredient_id: '', quantity: 1 }]);
  };
  const updateRecipeRow = (idx, field, val) => {
    const r = [...editingRecipe];
    r[idx] = { ...r[idx], [field]: val };
    setEditingRecipe(r);
  };
  const removeRecipeRow = (idx) => {
    setEditingRecipe(editingRecipe.filter((_, i) => i !== idx));
  };

  const saveRecipe = async () => {
    const cleanRecipe = editingRecipe.filter(r => r.ingredient_id && r.quantity > 0).map(r => ({ ingredient_id: r.ingredient_id, quantity: parseInt(r.quantity) || 1 }));
    try {
      const payload = cleanRecipe.length === 0 ? { clear_recipe: true } : { recipe: cleanRecipe };
      await axios.put(`${API}/admin/menu/${editingRecipeId}`, payload, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Oppskrift lagret');
      setEditingRecipeId(null);
      fetchAll();
    } catch (e) { toast.error('Kunne ikke lagre oppskrift'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  const ingMap = Object.fromEntries(ingredients.map(i => [i.id, i]));
  const itemsWithRecipe = menuItems.filter(m => (m.recipe || []).length > 0);

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-inventory-page">
      <header className="bg-primary text-white py-4 px-6 flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-xl font-serif font-bold">Sydnie's Meny - Admin</h1>
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/dashboard')}>Bestillinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/menu')}>Meny</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/customers')}>Kunder</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/messages')}>Meldinger</Button>
            <Button variant="ghost" size="sm" className="text-white bg-white/20">📦 Inventar</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/settings')}>Innstillinger</Button>
          </div>
        </div>
        <Button variant="ghost" className="text-white hover:text-white/80" onClick={() => { localStorage.removeItem('adminToken'); navigate('/admin'); }}><LogOut className="w-4 h-4 mr-2" />Logg ut</Button>
      </header>

      <div className="p-6 max-w-5xl mx-auto space-y-8">
        {/* Ingredients section */}
        <div>
          <h2 className="text-2xl font-serif font-bold mb-1">📦 Ingredienser / Lager</h2>
          <p className="text-sm text-muted-foreground mb-4">Legg til ingredienser som har lager. Hver gang en kunde bestiller en rett, trekkes ingrediensene automatisk fra lageret.</p>

          {/* Add new ingredient */}
          <Card className="p-4 bg-purple-50 border-purple-200 mb-4">
            <div className="flex gap-2 flex-wrap">
              <Input placeholder="Ingrediens-navn (f.eks. Pansit Bihon)" value={newName} onChange={e => setNewName(e.target.value)} className="flex-1 min-w-[200px]" data-testid="new-ing-name" />
              <Input type="number" min="0" placeholder="Lager" value={newStock} onChange={e => setNewStock(e.target.value)} className="w-24" data-testid="new-ing-stock" />
              <Button onClick={addIngredient} className="bg-purple-600 hover:bg-purple-700" data-testid="add-ing-btn">+ Legg til</Button>
            </div>
          </Card>

          <div className="bg-white rounded-xl overflow-hidden shadow-sm border">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 font-semibold text-sm border-b">
              <div className="col-span-5">Ingrediens</div>
              <div className="col-span-4 text-center">Lager</div>
              <div className="col-span-2 text-center">Skjul ved 0</div>
              <div className="col-span-1"></div>
            </div>
            {ingredients.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">Ingen ingredienser ennå. Legg til en over.</div>
            ) : ingredients.map(ing => (
              <div key={ing.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b hover:bg-gray-50" data-testid={`ing-row-${ing.id}`}>
                <div className="col-span-5 font-semibold">{ing.name}</div>
                <div className="col-span-4 flex items-center justify-center gap-2">
                  <Button size="sm" variant="outline" className="w-8 h-8 p-0" onClick={() => updateStock(ing, Math.max(0, ing.stock - 1))} disabled={savingId === ing.id} data-testid={`ing-dec-${ing.id}`}>−</Button>
                  <Input
                    type="number"
                    min="0"
                    value={ing.stock}
                    onChange={e => setIngredients(prev => prev.map(i => i.id === ing.id ? { ...i, stock: parseInt(e.target.value) || 0 } : i))}
                    onBlur={e => updateStock(ing, parseInt(e.target.value) || 0)}
                    className={`w-20 text-center font-bold ${ing.stock === 0 ? 'text-red-600' : ing.stock <= 5 ? 'text-yellow-600' : 'text-green-700'}`}
                    data-testid={`ing-stock-${ing.id}`}
                  />
                  <Button size="sm" variant="outline" className="w-8 h-8 p-0" onClick={() => updateStock(ing, ing.stock + 1)} disabled={savingId === ing.id} data-testid={`ing-inc-${ing.id}`}>+</Button>
                </div>
                <div className="col-span-2 text-center">
                  <button
                    onClick={() => toggleHide(ing)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${ing.hide_when_empty !== false ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                    data-testid={`ing-hide-${ing.id}`}
                  >
                    {ing.hide_when_empty !== false ? '✅ Ja' : '❌ Nei'}
                  </button>
                </div>
                <div className="col-span-1 text-right">
                  <button onClick={() => deleteIngredient(ing)} className="text-red-500 hover:text-red-700" data-testid={`ing-del-${ing.id}`}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recipes section */}
        <div>
          <h2 className="text-2xl font-serif font-bold mb-1">🍽️ Oppskrifter</h2>
          <p className="text-sm text-muted-foreground mb-4">Knytt retter til ingredienser. Eksempel: "Pansit med vårruller" = 1 Pansit + 3 Kjøtt vårull. Lager trekkes automatisk ved bestilling.</p>

          {ingredients.length === 0 ? (
            <Card className="p-6 text-center bg-yellow-50 border-yellow-200">
              <p className="text-yellow-800">Du må legge til ingredienser først før du kan lage oppskrifter.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {menuItems.map(item => {
                const recipe = item.recipe || [];
                const isEditing = editingRecipeId === item.id;
                return (
                  <Card key={item.id} className="p-4" data-testid={`recipe-card-${item.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-3">
                        {item.image_url && <img src={getImageUrl(item.image_url)} alt={item.name} className="w-12 h-12 object-cover rounded-lg" />}
                        <div>
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.category} · {item.menu_type === 'today' ? 'I DAG' : item.menu_type === 'later' ? 'SENERE' : item.menu_type === 'special' ? 'SKJERVØY DAGA' : 'SNART'}</p>
                        </div>
                      </div>
                      {!isEditing && (
                        <Button size="sm" variant="outline" onClick={() => openRecipeEditor(item)} data-testid={`edit-recipe-${item.id}`}>
                          {recipe.length > 0 ? '✏️ Rediger oppskrift' : '+ Legg til oppskrift'}
                        </Button>
                      )}
                    </div>
                    
                    {!isEditing && recipe.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 ml-15">
                        {recipe.map((r, i) => {
                          const ing = ingMap[r.ingredient_id];
                          if (!ing) return null;
                          const isEmpty = ing.stock === 0;
                          return (
                            <Badge key={i} className={isEmpty ? 'bg-red-100 text-red-800 border-red-300' : 'bg-purple-100 text-purple-800 border-purple-300'}>
                              {r.quantity}× {ing.name} {isEmpty && '(TOMT)'}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                    
                    {!isEditing && recipe.length === 0 && (
                      <p className="text-xs text-muted-foreground italic ml-15">Ingen oppskrift - bruker eget lager (eller ubegrenset)</p>
                    )}

                    {isEditing && (
                      <div className="bg-purple-50 rounded-lg p-3 mt-2 space-y-2">
                        {editingRecipe.map((r, idx) => (
                          <div key={idx} className="flex gap-2 items-center" data-testid={`recipe-row-${idx}`}>
                            <Select value={r.ingredient_id} onValueChange={v => updateRecipeRow(idx, 'ingredient_id', v)}>
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Velg ingrediens..." />
                              </SelectTrigger>
                              <SelectContent>
                                {ingredients.map(ing => (
                                  <SelectItem key={ing.id} value={ing.id}>{ing.name} (lager: {ing.stock})</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input type="number" min="1" value={r.quantity} onChange={e => updateRecipeRow(idx, 'quantity', e.target.value)} className="w-20" data-testid={`recipe-qty-${idx}`} />
                            <span className="text-xs text-muted-foreground">stk</span>
                            <button onClick={() => removeRecipeRow(idx)} className="text-red-500">🗑️</button>
                          </div>
                        ))}
                        <Button size="sm" variant="outline" onClick={addRecipeRow} data-testid="add-recipe-row">+ Legg til ingrediens</Button>
                        <div className="flex gap-2 pt-2 border-t border-purple-200">
                          <Button size="sm" onClick={saveRecipe} className="bg-purple-600 hover:bg-purple-700" data-testid="save-recipe">💾 Lagre</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingRecipeId(null)}>Avbryt</Button>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">💡 Tips: Hvis "Pansit Bihon" er tomt, vil alle retter som bruker Pansit Bihon i oppskriften automatisk skjules for kundene.</p>
      </div>
    </div>
  );
};


// Admin Messages Page
const AdminMessagesPage = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('adminToken');
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!adminToken) { navigate('/admin'); return; }
    fetchMessages();
  }, [adminToken, navigate]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setMessages(res.data.messages || []);
      setUnread(res.data.unread || 0);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem('adminToken'); navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/admin/messages/${id}/read`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      fetchMessages();
    } catch (e) { toast.error('Kunne ikke markere som lest'); }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Slett denne meldingen?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success('Melding slettet');
      fetchMessages();
    } catch (e) {
      toast.error('Kunne ikke slette melding');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-secondary" /></div>;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-messages-page">
      <header className="bg-primary text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-serif font-bold">Sydnie's Meny - Admin</h1>
          <div className="flex gap-2 flex-wrap">
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/dashboard')}>Bestillinger</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/menu')}>Meny</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/customers')}>Kunder</Button>
            <Button variant="ghost" size="sm" className="text-white bg-white/20">Meldinger {unread > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unread}</span>}</Button>
            <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" onClick={() => navigate('/admin/settings')}>Innstillinger</Button>
          </div>
        </div>
        <Button variant="ghost" className="text-white hover:text-white/80" onClick={() => { localStorage.removeItem('adminToken'); navigate('/admin'); }}><LogOut className="w-4 h-4 mr-2" />Logg ut</Button>
      </header>

      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-serif font-bold">Meldinger fra kunder</h2>
            <p className="text-sm text-muted-foreground">{messages.length} totalt · {unread} ulest</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchMessages} data-testid="refresh-messages"><RefreshCw className="w-4 h-4 mr-2" />Oppdater</Button>
        </div>

        {messages.length === 0 ? (
          <Card className="p-8 text-center"><p className="text-muted-foreground">Ingen meldinger ennå</p></Card>
        ) : (
          <div className="space-y-3">
            {messages.map(m => (
              <Card key={m.id} className={`p-5 ${!m.read ? 'border-l-4 border-l-secondary bg-secondary/5' : ''}`} data-testid={`admin-message-${m.id}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{m.name}</h3>
                      {!m.read && <Badge className="bg-secondary text-white text-xs">NY</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 space-x-3">
                      {m.email && <a href={`mailto:${m.email}`} className="hover:text-secondary break-all">{m.email}</a>}
                      {m.phone && <a href={`tel:${m.phone}`} className="hover:text-secondary">{m.phone}</a>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(m.created_at).toLocaleString('nb-NO', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {!m.read && (
                      <Button variant="outline" size="sm" onClick={() => markAsRead(m.id)} data-testid={`mark-read-${m.id}`}>Markér lest</Button>
                    )}
                    <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => deleteMessage(m.id)} disabled={deletingId === m.id} data-testid={`delete-message-${m.id}`}>
                      {deletingId === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 whitespace-pre-wrap text-sm">{m.message}</div>
                {m.email && (
                  <div className="mt-3">
                    <a href={`mailto:${m.email}?subject=Re: Din melding til Sydnie's Meny`} className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
                      <Mail className="w-4 h-4" />Svar på e-post
                    </a>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// Main App
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <div className="App">
            <div className="noise-overlay" />
          <BrowserRouter>
            <Routes>
              <Route path="/admin" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/menu" element={<AdminMenuPage />} />
              <Route path="/admin/customers" element={<AdminCustomersPage />} />
              <Route path="/admin/messages" element={<AdminMessagesPage />} />
              <Route path="/admin/inventory" element={<AdminInventoryPage />} />
              <Route path="/admin/settings" element={<AdminSettingsPage />} />
              <Route path="/*" element={
                <>
                  <Header />
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/menu" element={<MenuPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                    <Route path="/orders" element={<UserOrdersPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/contact" element={<ContactPage />} />
                  </Routes>
                  <MobileNav />
                </>
              } />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
          </div>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
